import { _ as __vitePreload } from 'theme/preload-helper';
import { A as A11y, a as Autoplay, N as Navigation, P as Pagination, S as Swiper, E as EffectFade } from 'theme/swiper';
import 'theme/vendor';
import 'theme/vendor-helpers';

class SliderComponent extends HTMLElement {
  connectedCallback() {
    this._config = JSON.parse(
      this._query('script[type="application/json"]')?.textContent ?? '{}'
    );
    const bp = parseInt(this.getAttribute('breakpoint')) || 768;
    this._breakpointMQ = window.matchMedia(`(min-width: ${bp}px)`);
    this._onBreakpoint = this._update.bind(this);
    this._breakpointMQ.addEventListener('change', this._onBreakpoint);
    this._bindEditorSelect();
    this._stageBar();

    if (this._eagerInit || window.Shopify?.designMode) {
      this._update();
      return
    }

    this._initObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        this._initObserver.disconnect();
        this._initObserver = null;
        this._update();
      },
      { rootMargin: '400px 0px' }
    );
    this._initObserver.observe(this);
  }

  disconnectedCallback() {
    this._initObserver?.disconnect();
    this._initObserver = null;
    this._offscreenObserver?.disconnect();
    this._offscreenObserver = null;
    this._barObserver?.disconnect();
    this._barObserver = null;
    clearTimeout(this._barTimer);
    this._breakpointMQ.removeEventListener('change', this._onBreakpoint);
    this._teardown();
  }

  _stageBar() {
    const bar = this._query('[carousel-bar]');
    if (!bar) return

    const sync = () => {
      const revealed = bar.classList.contains('is-revealed');
      if (revealed === this._barRevealed) return
      this._barRevealed = revealed;
      clearTimeout(this._barTimer);
      if (!revealed) {
        bar.classList.remove('is-entered');
        return
      }
      const delay = parseFloat(bar.style.getPropertyValue('--reveal-delay')) || 0;
      const offset = parseInt(bar.getAttribute('bar-fill-offset'), 10) || 0;
      bar.style.setProperty('--bar-fill-offset', `${offset}ms`);
      this._barTimer = setTimeout(() => {
        bar.style.setProperty('--reveal-delay', '0ms');
        bar.style.removeProperty('--bar-fill-offset');
        bar.classList.add('is-entered');
      }, delay + offset + 1000);
    };

    this._barRevealed = null;
    this._barObserver = new MutationObserver(sync);
    this._barObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  _replayBar() {
    const bar = this._query('[carousel-bar]');
    if (!bar?.classList.contains('is-revealed')) return
    bar.classList.remove('is-revealed');
    void bar.offsetWidth;
    bar.classList.add('is-revealed');
  }

  get _eagerInit() {
    return this.hasAttribute('eager-init')
  }

  boot() {
    this._initObserver?.disconnect();
    this._initObserver = null;
    const booted = !!this.swiper;
    this._update();
    if (booted) this.swiper?.update();
  }

  _bindEditorSelect() {
    if (!window.Shopify?.designMode || this._selectBound) return
    this._selectBound = true;
    this.addEventListener('shopify:block:select', (event) => {
      if (!this.swiper) return
      const slide = event.target.closest(this._slideSelector);
      const realSlides = this._queryAll(`${this._slideSelector}:not(.swiper-slide-duplicate)`);
      const index = realSlides.indexOf(slide);
      if (index > -1) this.swiper.slideToLoop(index);
    });
  }

  get _modules() {
    return [A11y, Autoplay, Navigation, Pagination]
  }

  get _swiperConfig() {
    return { watchOverflow: true }
  }

  get _shouldEnable() {
    const hasBreakpointAttrs = this.hasAttribute('enable-desktop') || this.hasAttribute('enable-mobile');
    if (!hasBreakpointAttrs) return true
    const attr = this._breakpointMQ.matches ? 'enable-desktop' : 'enable-mobile';
    return this.hasAttribute(attr)
  }

  _update() {
    if (this._initObserver) return
    this._shouldEnable ? this._setup() : this._teardown();
  }

  get _slideSelector() {
    return '[slide]'
  }

  _query(selector) {
    return this.querySelector(selector)
  }

  _queryAll(selector) {
    return [...this.querySelectorAll(selector)]
  }

  _buildPagination() {
    return this._query('[slider-pagination]')
  }

  _buildPaginationConfig() {
    const el = this._buildPagination();
    if (!el) return false
    return {
      el,
      type: this.getAttribute('pagination-type') ?? 'bullets',
      clickable: true,
    }
  }

  _setup() {
    if (this.swiper) return

    this._swiperContainer = this._query('[swiper]');
    if (!this._swiperContainer) return

    const slides = this._queryAll(this._slideSelector);
    if (!slides.length) return

    this._controls = this._query('[slider-controls]') ?? null;
    const root = this._controls ?? this;

    const speed = parseInt(this.getAttribute('slide-speed')) || 600;

    const nextEl = this._controls ? root.querySelector('[slider-next]') : this._query('[slider-next]');
    const prevEl = this._controls ? root.querySelector('[slider-prev]') : this._query('[slider-prev]');

    this.swiper = new Swiper({
      el: this._swiperContainer,
      modules: this._modules,
      observer: true,
      observeParents: true,
      ...this._config,
      speed,
      navigation: nextEl && prevEl ? { nextEl, prevEl } : false,
      pagination: this._buildPaginationConfig(),
      a11y: {
        prevSlideMessage: prevEl?.getAttribute('aria-label'),
        nextSlideMessage: nextEl?.getAttribute('aria-label'),
      },
      ...this._swiperConfig,
    });

    if (this._controls) {
      const syncLock = () => this._controls.classList.toggle('is-hidden', !!this.swiper.isLocked);
      this.swiper.on('lock', syncLock);
      this.swiper.on('unlock', syncLock);
      this._replayBar();
      syncLock();
    }

    if (this._query('video-player')) {
      const playersIn = (slide) => {
        if (!slide) return []
        return slide.matches('video-player') ? [slide] : [...slide.querySelectorAll('video-player')]
      };

      const suspendInactive = () => {
        const active = this.swiper.slides[this.swiper.activeIndex];
        for (const player of this._queryAll('video-player')) {
          const isActive = player === active || active?.contains(player);
          player.toggleAttribute('autoplay-suspended', !isActive);
        }
      };
      suspendInactive();

      this.swiper.on('slideChange', () => {
        suspendInactive();

        for (const player of playersIn(this.swiper.slides[this.swiper.previousIndex])) {
          player.pause();
        }

        for (const player of playersIn(this.swiper.slides[this.swiper.activeIndex])) {
          if (player.hasAttribute('autoplay')) player.play();
        }
      });
    }

    if (this.hasAttribute('edge-fade')) {
      const syncFade = () => {
        this._query('[fade-start]')?.classList.toggle('is-visible', !this.swiper.isBeginning);
        this._query('[fade-end]')?.classList.toggle('is-visible', !this.swiper.isEnd);
      }
      ;['progress', 'resize', 'lock', 'unlock'].forEach((e) => this.swiper.on(e, syncFade));
      syncFade();
    }

    if (this._config.autoplay) {
      this.classList.add('slider--autoplay');
      this._bindAutoplayHold();
      this._bindOffscreenHold();
    }
  }

  _bindOffscreenHold() {
    this._offscreenObserver?.disconnect();
    this._stoppedOffscreen = false;
    this._offscreenObserver = new IntersectionObserver(([entry]) => {
      const autoplay = this.swiper?.autoplay;
      if (!autoplay) return
      if (!entry.isIntersecting) {
        if (!autoplay.running) return
        autoplay.stop();
        this._stoppedOffscreen = true;
      } else if (this._stoppedOffscreen) {
        this._stoppedOffscreen = false;
        autoplay.start();
        this.swiper.pagination?.el?.getAnimations?.({ subtree: true }).forEach((a) => (a.currentTime = 0));
      }
    });
    this._offscreenObserver.observe(this);
  }

  _bindAutoplayHold() {
    const autoplay = this.swiper.params.autoplay;
    if (!autoplay?.enabled || !autoplay.pauseOnMouseEnter) return

    const sync = () => this.swiper.pagination?.el?.classList.toggle('is-autoplay-held', this.swiper.autoplay.paused);
    this.swiper.on('autoplayPause', sync);
    this.swiper.on('autoplayResume', sync);

    if (this._holdEl === this.swiper.el) return
    const el = (this._holdEl = this.swiper.el);
    const relay = (type) => () => el.dispatchEvent(new PointerEvent(type, { pointerType: 'mouse' }));
    el.addEventListener('touchstart', relay('pointerenter'), { passive: true });
    el.addEventListener('touchend', relay('pointerleave'), { passive: true });
    el.addEventListener('touchcancel', relay('pointerleave'), { passive: true });
  }

  _teardown() {
    if (!this.swiper) return

    this.swiper.pagination?.el?.classList?.remove('is-autoplay-held');
    this.swiper.destroy(true, true);
    this._controls?.classList.remove('is-hidden');
    this.swiper = null;
    this._swiperContainer = null;
    this._controls = null;
  }
}

customElements.define('slider-component', SliderComponent);

class NestedCarousel extends SliderComponent {
  _isNestedSliderHost(node) {
    return node.localName.includes('-') && node.querySelector('[swiper]') != null
  }

  _owns(el) {
    let node = el.parentElement;
    while (node && node !== this) {
      if (this._isNestedSliderHost(node)) return false
      node = node.parentElement;
    }
    return node === this
  }

  _query(selector) {
    for (const el of this.querySelectorAll(selector)) {
      if (this._owns(el)) return el
    }
    return null
  }

  _queryAll(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => this._owns(el))
  }
}

if (!customElements.get('nested-carousel')) {
  customElements.define('nested-carousel', NestedCarousel);
}

class MediaCarousel extends SliderComponent {
  get _modules() {
    return [...super._modules, Autoplay, EffectFade]
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('autoplay-on-hover')) {
      this._onEnter = (event) => {
        if (event.pointerType !== 'mouse') return
        const autoplay = this.swiper?.autoplay;
        if (autoplay?.running) autoplay.stop();
        autoplay?.start();
      };
      this._onLeave = (event) => {
        if (event.pointerType !== 'mouse') return
        const swiper = this.swiper;
        if (!swiper) return
        swiper.autoplay?.stop();
        if (swiper.realIndex !== 0) swiper.slideToLoop(0);
      };
      this.addEventListener('pointerenter', this._onEnter);
      this.addEventListener('pointerleave', this._onLeave);
    }
  }

  disconnectedCallback() {
    if (this._onEnter) {
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
    }
    super.disconnectedCallback();
  }

  _setup() {
    super._setup();
    if (this.hasAttribute('autoplay-on-hover')) this.swiper?.autoplay?.stop();
  }

  _bindOffscreenHold() {
    if (!this.hasAttribute('autoplay-on-hover')) super._bindOffscreenHold();
  }
}

if (!customElements.get('media-carousel')) {
  customElements.define('media-carousel', MediaCarousel);
}

class ImageLoading extends HTMLImageElement {
  constructor() {
    super();
    this.afterImageLoaded = this.afterImageLoaded.bind(this);
  }

  connectedCallback() {
    if (this.closest('.swiper-slide-duplicate')) {
      this.afterImageLoaded();
      return;
    }

    this.addEventListener('load', this.afterImageLoaded);

    if (this.complete) this.afterImageLoaded();
  }

  disconnectedCallback() {
    this.removeEventListener('load', this.afterImageLoaded);
  }

  afterImageLoaded() {
    this.closest('[image-container]')?.classList.add('image-loaded');
  }
}

if (!customElements.get('image-loading')) {
  customElements.define('image-loading', ImageLoading, { extends: 'img' });
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function cssTimeMs(el, prop, fallback = 0) {
  const value = getComputedStyle(el).getPropertyValue(prop).trim();
  const n = parseFloat(value);
  if (!value || Number.isNaN(n)) return fallback
  return value.endsWith('ms') ? n : n * 1000
}



const FALLBACK_DURATION = 240;
const FALLBACK_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
let timing = null;

const readTiming = () => {
  if (timing) return timing
  const root = document.documentElement;
  const duration = cssTimeMs(root, '--dur-base', FALLBACK_DURATION);
  const easing = getComputedStyle(root).getPropertyValue('--ease-smooth').trim() || FALLBACK_EASING;
  timing = { duration, easing };
  return timing
};

class Accordion extends HTMLDetailsElement {
  #summary = null
  #content = null
  #animation = null
  #isClosing = false
  #isExpanding = false

  connectedCallback() {
    this.#summary = this.querySelector(':scope > summary');
    this.#content = this.#summary?.nextElementSibling ?? null;
    if (!this.#summary || !this.#content) return
    this.#summary.addEventListener('click', this.#onClick);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);

    const openFirstContainer = this.closest('[open-first-item]');
    if (openFirstContainer && !this.open && openFirstContainer.querySelector('[accordion-item]') === this) {
      this.open = true;
    }
  }

  disconnectedCallback() {
    this.#summary?.removeEventListener('click', this.#onClick);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.#animation?.cancel();
  }

  #onBlockSelect = () => this.show()

  #onClick = (event) => {
    if (reducedMotion.matches) {
      if (!this.open) this.#closeSiblings();
      return
    }
    event.preventDefault();
    this.style.overflow = 'hidden';
    if (this.#isClosing || !this.open) {
      this.#expand();
    } else if (this.#isExpanding || this.open) {
      this.#collapse();
    }
  }

  close() {
    if (!this.open || this.#isClosing) return
    if (reducedMotion.matches) {
      this.open = false;
      return
    }
    this.style.overflow = 'hidden';
    this.#collapse();
  }

  show() {
    if (this.open && !this.#isClosing) return
    if (!this.#summary || !this.#content || reducedMotion.matches) {
      this.#closeSiblings();
      this.open = true;
      return
    }
    this.style.overflow = 'hidden';
    this.#expand();
  }

  resize(change) {
    if (!this.open || this.#isClosing || !this.#content || reducedMotion.matches) {
      change();
      return
    }
    if (this.#isExpanding) {
      change();
      this.#expand();
      return
    }
    const startHeight = `${this.offsetHeight}px`;
    change();
    const endHeight = `${this.#summary.offsetHeight + this.#content.offsetHeight + this.#borders()}px`;
    if (startHeight === endHeight) return
    this.style.overflow = 'hidden';
    this.#run(startHeight, endHeight, true);
  }

  #closeSiblings() {
    const group = this.closest('[single-open]');
    if (!group) return
    for (const other of group.querySelectorAll('details[is="accordion-details"][open]')) {
      if (other !== this && other.closest('[single-open]') === group && other instanceof Accordion) other.close();
    }
  }

  #expand() {
    this.#isClosing = false;
    this.#isExpanding = true;
    this.classList.remove('is-closing');
    this.#closeSiblings();
    const startHeight = `${this.offsetHeight}px`;
    this.open = true;
    const endHeight = `${this.#summary.offsetHeight + this.#content.offsetHeight + this.#borders()}px`;
    this.#run(startHeight, endHeight, true);
  }

  #collapse() {
    this.#isExpanding = false;
    this.#isClosing = true;
    this.classList.add('is-closing');
    const startHeight = `${this.offsetHeight}px`;
    const endHeight = `${this.#summary.offsetHeight + this.#borders()}px`;
    this.#run(startHeight, endHeight, false);
  }

  #borders() {
    return this.offsetHeight - this.clientHeight
  }

  #run(startHeight, endHeight, open) {
    this.#animation?.cancel();
    const { duration: base, easing } = readTiming();
    const duration = cssTimeMs(this, '--accordion-duration', base);
    const animation = this.animate({ height: [startHeight, endHeight] }, { duration, easing });
    this.#animation = animation;
    animation.onfinish = () => this.#onFinish(open);
    animation.oncancel = () => {
      if (this.#animation !== animation) return
      this.#isExpanding = false;
      this.#isClosing = false;
    };
  }

  #onFinish(open) {
    this.open = open;
    this.classList.remove('is-closing');
    this.#animation = null;
    this.#isExpanding = false;
    this.#isClosing = false;
    this.style.height = '';
    this.style.overflow = '';
  }
}

if (!customElements.get('accordion-details')) {
  customElements.define('accordion-details', Accordion, { extends: 'details' });
}

function onVisible(element, onEnter, { once = true, onLeave, root = null, rootMargin = '0px', threshold = 0 } = {}) {
  if (!element) return null

  if (!('IntersectionObserver' in window)) {
    onEnter?.(element);
    return null
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          onEnter?.(entry.target, entry);
          if (once) obs.unobserve(entry.target);
        } else {
          onLeave?.(entry.target, entry);
        }
      }
    },
    { root, rootMargin, threshold }
  );

  observer.observe(element);
  return observer
}



class VideoPlayer extends HTMLElement {
  #video = null
  #cover = null
  #mobileCover = null
  #playBtn = null
  #overlay = null
  #observer = null
  #bound = false
  #embedContainer = null
  #mql = null
  #onMqlChange = () => { this.#loadSrc(); }
  #onEmbedMessage = (event) => { this.#readEmbedState(event); }

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#mql?.removeEventListener('change', this.#onMqlChange);
    this.#mql = null;
    window.removeEventListener('message', this.#onEmbedMessage);
  }


  pause() {
    if (this.#embedContainer) {
      if (!this.#postToEmbed('pause')) this.#unloadEmbed();
    } else {
      this.#video?.pause();
    }
  }

  play() {
    if (this.#embedContainer) {
      if (this.#postToEmbed('play')) return
      this.#activateEmbed(true);
    } else if (this.#video) {
      this.#loadSrc();
      this.#setLoading(true);
      this.#video.play().catch(() => this.#setLoading(false));
    }
  }


  #init() {
    this.#video = this.querySelector('[data-video]');
    this.#cover = this.querySelector('[data-cover]');
    this.#mobileCover = this.querySelector('[data-mobile-cover]');
    this.#playBtn = this.querySelector('[data-play-btn]');
    this.#overlay = this.querySelector('[data-overlay]');
    this.#embedContainer = this.querySelector('[data-embed-container]');

    const firstInit = !this.#bound;
    this.#bound = true;

    if (this.#embedContainer) {
      if (firstInit) this.#bindEmbedPlayToggle();
      this.#watchEmbedState();
      if (this.hasAttribute('autoplay')) {
        this.#observer = onVisible(
          this,
          () => {
            if (this.hasAttribute('autoplay-suspended')) return
            this.#activateEmbed(true);
            this.#observer?.disconnect();
          },
          { once: false }
        );
      }
    } else if (this.#video) {
      this.#initNative(firstInit);
    }

  }


  #bindEmbedPlayToggle() {
    this.#playBtn?.addEventListener('click', () => {
      if (!this.#postToEmbed('play')) this.#activateEmbed();
    });
  }

  #postToEmbed(action) {
    const iframe = this.#embedContainer?.querySelector('iframe');
    if (!iframe?.contentWindow) return false

    const payload = this.#embedContainer.dataset.embedType === 'vimeo'
      ? JSON.stringify({ method: action })
      : JSON.stringify({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: '' });

    iframe.contentWindow.postMessage(payload, '*');
    return true
  }

  #activateEmbed(muted = false) {
    const container = this.#embedContainer;
    if (!container) return

    if (container.querySelector('iframe')) return

    const baseUrl = container.dataset.embedUrl;
    const type = container.dataset.embedType;
    const title = container.dataset.embedTitle;

    if (!baseUrl) return

    this.#setLoading(true);

    const separator = baseUrl.includes('?') ? '&' : '?';
    let src = baseUrl + separator + 'autoplay=1';
    if (muted) src += type === 'vimeo' ? '&muted=1' : '&mute=1';
    const iframe = document.createElement('iframe');
    iframe.id = `video-embed-${Math.random().toString(36).slice(2)}`;
    iframe.src = src;
    iframe.title = title;
    iframe.allow = 'autoplay; encrypted-media';
    iframe.allowFullscreen = true;

    iframe.addEventListener('load', () => {
      this.#revealEmbed();
      this.#subscribeEmbed(iframe);
    }, { once: true });
    container.appendChild(iframe);
  }

  #watchEmbedState() {
    window.addEventListener('message', this.#onEmbedMessage);
    const iframe = this.#embedContainer.querySelector('iframe');
    if (!iframe) return
    iframe.addEventListener('load', () => this.#subscribeEmbed(iframe), { once: true });
    this.#subscribeEmbed(iframe);
  }

  #subscribeEmbed(iframe) {
    const post = (message) => iframe.contentWindow?.postMessage(JSON.stringify(message), '*');
    if (this.#embedContainer.dataset.embedType === 'vimeo') {
      for (const value of ['play', 'pause', 'ended']) post({ method: 'addEventListener', value });
    } else {
      post({ event: 'listening', id: iframe.id, channel: 'widget' });
    }
  }

  #readEmbedState(event) {
    const iframe = this.#embedContainer?.querySelector('iframe');
    if (!iframe || event.source !== iframe.contentWindow) return

    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return
      }
    }

    let playing = null;
    if (this.#embedContainer.dataset.embedType === 'vimeo') {
      if (data?.event === 'ready') this.#subscribeEmbed(iframe);
      if (data?.event === 'play') playing = true;
      if (data?.event === 'pause' || data?.event === 'ended') playing = false;
    } else {
      const state = data?.info?.playerState;
      if (state === 1) playing = true;
      if (state === 2 || state === -1 || state === 5) playing = false;
      if (state === 0 && !this.hasAttribute('loop')) playing = false;
    }

    if (playing === null) return
    this.#setPlayingWithControls(playing && !this.hasAttribute('keep-overlay'));
  }

  #revealEmbed() {
    this.#setLoading(false);
    this.#cover?.classList.add('is-hidden');
    this.#mobileCover?.classList.add('is-hidden');
    this.#hideOverlay();
    this.#hidePlayBtn();
    this.#setPlayingWithControls(!this.hasAttribute('keep-overlay'));
  }

  #hideOverlay() {
    if (this.hasAttribute('keep-overlay')) return
    this.#overlay?.classList.add('is-hidden');
  }

  #setPlayingWithControls(on) {
    if (this.hasAttribute('playing-with-controls') === on) return
    this.toggleAttribute('playing-with-controls', on);
    this.dispatchEvent(
      new CustomEvent('video:controls', { bubbles: true, composed: true, detail: { playing: on } })
    );
  }

  #setLoading(on) {
    this.#playBtn?.classList.toggle('is-loading', on);
  }

  #hidePlayBtn() {
    this.#playBtn?.classList.add('is-hidden');
    if (this.#playBtn) {
      this.#playBtn.setAttribute('aria-hidden', 'true');
      this.#playBtn.setAttribute('tabindex', '-1');
    }
  }

  #unloadEmbed() {
    this.#embedContainer?.querySelector('iframe')?.remove();
    this.#setLoading(false);
    this.#setPlayingWithControls(false);
    this.#cover?.classList.remove('is-hidden');
    this.#mobileCover?.classList.remove('is-hidden');
    this.#overlay?.classList.remove('is-hidden');
    this.#playBtn?.classList.remove('is-hidden');

    if (this.#playBtn) {
      this.#playBtn.removeAttribute('aria-hidden');
      this.#playBtn.removeAttribute('tabindex');
    }
  }


  #initNative(firstInit) {
    if (this.hasAttribute('muted')) this.#video.muted = true;

    if (firstInit) {
      this.#bindEvents();
      this.#bindPlayToggle();
    }

    if (this.hasAttribute('autoplay')) {
      this.#setupAutoplay();
    }
  }

  #loadSrc() {
    if (!this.dataset.desktopVideo) return

    if (this.dataset.mobileVideo && !this.#mql) {
      this.#mql = window.matchMedia('(max-width: 749px)');
      this.#mql.addEventListener('change', this.#onMqlChange);
    }

    const url = (this.#mql?.matches && this.dataset.mobileVideo)
      ? this.dataset.mobileVideo
      : this.dataset.desktopVideo;

    if (!url) return

    const source = this.#video.querySelector('source');
    if (source && source.getAttribute('src') !== url) {
      source.src = url;
      this.#video.load();
    }
  }

  #bindEvents() {
    this.#video.addEventListener('playing', () => this.#onPlaying());
    this.#video.addEventListener('pause', () => this.#onPause());
    this.#video.addEventListener('ended', () => this.#onEnded());
  }

  #bindPlayToggle() {
    this.#playBtn?.addEventListener('click', () => this.#toggle());
  }

  #setupAutoplay() {
    this.#observer?.disconnect();
    this.#observer = onVisible(
      this,
      () => {
        if (this.hasAttribute('autoplay-suspended')) return
        this.#loadSrc();
        this.#video.play().catch(() => {});
      },
      { once: false, onLeave: () => this.#video.pause(), threshold: 0.5 }
    );
  }


  #toggle() {
    if (this.#video.paused) {
      this.#pauseOthers();
      this.#loadSrc();
      this.#setLoading(true);
      this.#video.play().catch(() => this.#setLoading(false));
    } else {
      this.#video.pause();
    }
  }

  #pauseOthers() {
    document.querySelectorAll('video-player').forEach((player) => {
      if (player === this || player.hasAttribute('autoplay')) return
      player.pause();
    });
  }


  #onPlaying() {
    this.#setLoading(false);
    this.#cover?.classList.add('is-hidden');
    this.#mobileCover?.classList.add('is-hidden');
    this.#hideOverlay();
    this.#setPlayingWithControls(this.#video.hasAttribute('controls'));

    if (this.#video.hasAttribute('controls') || this.hasAttribute('autoplay')) {
      this.#playBtn?.classList.add('is-hidden');
    } else {
      this.#setPlayBtnPlaying(true);
    }
  }

  #onPause() {
    this.#setLoading(false);
    this.#setPlayingWithControls(false);
    this.#overlay?.classList.remove('is-hidden');
    this.#playBtn?.classList.remove('is-hidden');
    this.#setPlayBtnPlaying(false);
  }

  #setPlayBtnPlaying(isPlaying) {
    if (!this.#playBtn) return
    this.#playBtn.classList.toggle('is-playing', isPlaying);
    const label = isPlaying
      ? this.#playBtn.dataset.pauseLabel ?? this.#playBtn.dataset.playLabel
      : this.#playBtn.dataset.playLabel;
    this.#playBtn.setAttribute('aria-label', label ?? 'Play');
  }

  #onEnded() {
    this.#setLoading(false);
    if (!this.hasAttribute('loop')) {
      this.#cover?.classList.remove('is-hidden');
      this.#mobileCover?.classList.remove('is-hidden');
    }
    this.#overlay?.classList.remove('is-hidden');
    this.#playBtn?.classList.remove('is-hidden');
  }
}

customElements.define('video-player', VideoPlayer);

const MAX_FONT_DESKTOP = 320;
const MAX_FONT_MOBILE = 80;
const DESKTOP_QUERY = '(min-width: 768px)';

class LargeText extends HTMLElement {
  connectedCallback() {
    this._fit = this._fit.bind(this);
    this._ro = new ResizeObserver(() => requestAnimationFrame(this._fit));
    this._ro.observe(this.parentElement || this);
    requestAnimationFrame(this._fit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this._fit);

    this._setupOverflowSpacing();
  }

  disconnectedCallback() {
    this._ro.disconnect();
    if (this._overflowRo) this._overflowRo.disconnect();
  }

  _fit() {
    const container = this.parentElement;
    if (!container || container.offsetWidth <= 0) return;

    const maxWidth = container.offsetWidth;
    const maxFont = window.matchMedia(DESKTOP_QUERY).matches ? MAX_FONT_DESKTOP : MAX_FONT_MOBILE;
    this.style.fontSize = '1px';

    let lo = 1, hi = maxFont;
    while (hi - lo > 0.5) {
      const mid = (lo + hi) / 2;
      this.style.fontSize = `${mid}px`;
      if (this.scrollWidth > maxWidth) hi = mid;
      else lo = mid;
    }

    this.style.fontSize = `${Math.min(lo * 0.99, maxFont)}px`;
  }

  _setupOverflowSpacing() {
    const wrapper = this.closest('[lmt-overflow]');
    if (!wrapper) return;

    const target = wrapper.closest('[lmt-spacing-target]') || wrapper.closest('.content-padding');
    if (!target) return;

    const update = () => {
      const offset = wrapper.offsetHeight / 2;
      target.style.marginBlockEnd = `${offset}px`;
      target.style.setProperty('--lmt-overflow-offset', `${offset}px`);
    };

    this._overflowRo = new ResizeObserver(() => requestAnimationFrame(update));
    this._overflowRo.observe(wrapper);
    requestAnimationFrame(update);
  }
}

customElements.define('large-text', LargeText);

const DEPTH = { bg: 70, img1: -110, img2: -70, content: -20, word: -120 };
const WORD_RESERVE = Math.abs(DEPTH.word) + 24;
const MOBILE_MAX = 768;

class MediaBanner extends HTMLElement {
  connectedCallback() {
    this._hoistFloatingImages();
    this._stepWord();

    if (!this.hasAttribute('data-parallax')) return;
    if (reducedMotion.matches) return;

    this._overflow = this.querySelector('[lmt-overflow]');
    this._reserveTarget = this.closest('[word-reserve]');
    this._layers = [
      [this.querySelector('[parallax-bg]'), DEPTH.bg],
      ...[...this.querySelectorAll('[parallax-layer="img1"]')].map((el) => [el, DEPTH.img1]),
      ...[...this.querySelectorAll('[parallax-layer="img2"]')].map((el) => [el, DEPTH.img2]),
      [this.querySelector('[parallax-content]'), DEPTH.content],
      [this._overflow, DEPTH.word],
    ].filter(([el]) => el);

    this._disableMobile = this.hasAttribute('data-parallax-disable-mobile');
    this._active = null;

    this._onScroll = this._onScroll.bind(this);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    this._onScroll();
  }

  _hoistFloatingImages() {
    const frame = this.querySelector('.media-banner__frame');
    const content = frame?.querySelector('[parallax-content]');
    if (!frame) return;
    this.querySelectorAll('.media-banner__floating-images').forEach((node) => {
      if (node.parentElement === frame) return;
      frame.insertBefore(node, content ?? null);
    });
  }

  _stepWord() {
    const group = this.querySelector('[reveal-group]');
    if (!group) return;
    const step = group.querySelectorAll('[reveal]').length;
    this.querySelectorAll('[banner-word]').forEach((word) => {
      word.setAttribute('reveal-step', String(step));
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
  }

  _onScroll() {
    if (this._disableMobile && window.innerWidth < MOBILE_MAX) {
      this._setActive(false);
      return;
    }
    this._setActive(true);

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const span = (vh + rect.height) / 2;
    if (span <= 0) return;

    const t = Math.max(-1, Math.min(1, (vh / 2 - (rect.top + rect.height / 2)) / span));
    this._layers.forEach(([el, depth]) => {
      el.style.setProperty('--py', `${(t * depth).toFixed(2)}px`);
    });
  }

  _setActive(on) {
    if (this._active === on) return;
    this._active = on;
    if (!on) this._layers.forEach(([el]) => el.style.removeProperty('--py'));
    if (this._overflow && this._reserveTarget) {
      this._reserveTarget.style.setProperty('--word-reserve', on ? `${WORD_RESERVE}px` : '0px');
    }
  }
}

customElements.define('media-banner', MediaBanner);

class ThemeEvents {
  static cartUpdate = 'cart:update'
  static freeShippingUpdate = 'free-shipping:update'
  static filterUpdate = 'filter:update'
  static facetsRendered = 'facets:rendered'
  static dropdownOpen = 'dropdown:open'
  static drawerOpen = 'drawer:open'
  static drawerClose = 'drawer:close'
  static variantSelected = 'variant:selected'
  static bundleAdded = 'bundle:added'
  static bundleRemoved = 'bundle:removed'
  static countdownEnded = 'countdown:ended'
  static tabGroupReady = 'tab-group:ready'
  static tabGroupChange = 'tab-group:change'
}

class FilterUpdateEvent extends Event {
  static eventName = ThemeEvents.filterUpdate

  constructor(params = new URLSearchParams(), sourceId) {
    super(FilterUpdateEvent.eventName, { bubbles: true });
    this.detail = { params, sourceId };
  }

  hasActiveFilters() {
    for (const key of this.detail.params.keys()) {
      if (key.startsWith('filter.')) return true
    }
    return false
  }
}

class FacetsRenderedEvent extends Event {
  static eventName = ThemeEvents.facetsRendered

  constructor() {
    super(FacetsRenderedEvent.eventName, { bubbles: true });
  }
}

class CartAddEvent extends Event {
  static eventName = ThemeEvents.cartUpdate

  constructor(resource = null, sourceId, data = {}) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
}

class CartUpdateEvent extends Event {
  static eventName = ThemeEvents.cartUpdate

  constructor(resource = null, sourceId, data = {}) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
}

class FreeShippingUpdateEvent extends Event {
  static eventName = ThemeEvents.freeShippingUpdate

  constructor(data = {}) {
    super(FreeShippingUpdateEvent.eventName, { bubbles: true });
    this.detail = { ...data };
  }
}

class DropdownOpenEvent extends Event {
  static eventName = ThemeEvents.dropdownOpen

  constructor(source) {
    super(DropdownOpenEvent.eventName);
    this.detail = source;
  }
}

class DrawerOpenEvent extends Event {
  static eventName = ThemeEvents.drawerOpen

  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true, composed: true });
  }
}

class DrawerCloseEvent extends Event {
  static eventName = ThemeEvents.drawerClose

  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true, composed: true });
  }
}

class VariantSelectedEvent extends Event {
  static eventName = ThemeEvents.variantSelected

  constructor(variantId = null) {
    super(VariantSelectedEvent.eventName, { bubbles: true });
    this.detail = { variantId };
  }
}

class BundleAddedEvent extends Event {
  static eventName = ThemeEvents.bundleAdded

  constructor(product, variant) {
    super(BundleAddedEvent.eventName, { bubbles: true });
    this.detail = { product, variant };
  }
}

class BundleRemovedEvent extends Event {
  static eventName = ThemeEvents.bundleRemoved

  constructor(variant) {
    super(BundleRemovedEvent.eventName, { bubbles: true });
    this.detail = { variant };
  }
}

class CountdownEndedEvent extends Event {
  static eventName = ThemeEvents.countdownEnded

  constructor(sectionId, blockId) {
    super(CountdownEndedEvent.eventName, { bubbles: true });
    this.detail = { sectionId, blockId };
  }
}

class TabGroupReadyEvent extends Event {
  static eventName = ThemeEvents.tabGroupReady

  constructor() {
    super(TabGroupReadyEvent.eventName, { bubbles: true });
  }
}

class TabGroupChangeEvent extends Event {
  static eventName = ThemeEvents.tabGroupChange

  constructor(index, tab, panel) {
    super(TabGroupChangeEvent.eventName, { bubbles: true });
    this.detail = { index, tab, panel };
  }
}

const rafThrottle = (fn, fallback = 120) => {
  let ticking = false;

  return () => {
    if (ticking) return
    ticking = true;

    let ran = false;
    const done = () => {
      if (ran) return
      ran = true;
      fn();
      ticking = false;
    };

    requestAnimationFrame(done);
    setTimeout(done, fallback);
  }
};


const STAGGER = 50;
const MAX_STEPS = 8;
const ROOT_MARGIN$1 = '0px 0px -60px 0px';

const tracked = new WeakSet();
const held = new WeakSet();
let pairs = [];
let observer = null;

const show = el => {
  if (!el.classList.contains('is-revealed')) el.classList.add('is-revealed');
};

const visible = el =>
  typeof el.checkVisibility === 'function'
    ? el.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true })
    : true;

const anchorFor = el => {
  const selector = el.getAttribute('reveal-with');
  if (!selector) return el
  const scope = el.closest('[reveal-scope], .shopify-section, section') || document;
  return scope.querySelector(selector) || el
};

const staggerFor = el => {
  const host = el.closest('[reveal-stagger]');
  const ms = host ? parseInt(host.getAttribute('reveal-stagger'), 10) : NaN;
  return Number.isNaN(ms) ? STAGGER : ms
};

const groupOf = el =>
  el.closest('[reveal-group]') || el.closest('[reveal-scope], .shopify-section') || el.parentElement;

const indexInGroup = el => {
  const group = groupOf(el);
  if (!group) return 0
  const members = [...group.querySelectorAll('[reveal]')].filter(m => groupOf(m) === group);
  return Math.max(0, members.indexOf(el))
};

const applyDelays = items => {
  items.forEach(el => {
    const step = el.hasAttribute('reveal-step')
      ? parseInt(el.getAttribute('reveal-step'), 10)
      : indexInGroup(el);
    const base = parseInt(el.getAttribute('reveal-delay'), 10) || 0;
    el.style.setProperty('--reveal-delay', `${base + Math.min(step, MAX_STEPS) * staggerFor(el)}ms`);
  });
};

const sweep = () => {
  const h = window.innerHeight || document.documentElement.clientHeight;
  const due = pairs.filter(
    p =>
      p.el.isConnected &&
      !p.el.classList.contains('is-revealed') &&
      p.watch.getBoundingClientRect().top < h - 60 &&
      visible(p.el)
  );
  due.forEach(p => show(p.el));
  pairs = pairs.filter(p => p.el.isConnected && !p.el.classList.contains('is-revealed'));
};

const ensureObserver = () => {
  if (observer || !('IntersectionObserver' in window)) return
  observer = new IntersectionObserver(
    entries => {
      const hit = new Set(entries.filter(e => e.isIntersecting).map(e => e.target));
      if (!hit.size) return
      pairs.filter(p => hit.has(p.watch) && visible(p.el)).forEach(p => show(p.el));
    },
    { rootMargin: ROOT_MARGIN$1, threshold: 0.05 }
  );
};

const initReveal = (root = document) => {
  const found = [...root.querySelectorAll('[reveal]')];
  if (root !== document && root.matches?.('[reveal]')) found.unshift(root);
  const items = found.filter(el => !tracked.has(el) && !held.has(el));
  if (!items.length) return

  items.forEach(el => tracked.add(el));
  applyDelays(items);

  if (!('IntersectionObserver' in window)) {
    items.forEach(show);
    return
  }

  ensureObserver();
  const fresh = items.map(el => ({ el, watch: anchorFor(el) }));
  pairs.push(...fresh);
  new Set(fresh.map(p => p.watch)).forEach(w => observer.observe(w));
  sweep();
};

const parkReveal = (root = document) => {
  const items = [...root.querySelectorAll('[reveal]')];
  if (root !== document && root.matches?.('[reveal]')) items.unshift(root);
  items.forEach(el => {
    el.style.transition = 'none';
    el.classList.remove('is-revealed');
    tracked.delete(el);
    held.add(el);
  });
  void document.body.offsetHeight;
  items.forEach(el => el.style.removeProperty('transition'));
};

const releaseReveal = (root = document, { force = false } = {}) => {
  const items = [...root.querySelectorAll('[reveal]')];
  if (root !== document && root.matches?.('[reveal]')) items.unshift(root);
  items.forEach(el => held.delete(el));
  initReveal(root);
  if (force) items.forEach(show);
};

const replayReveal = (root = document, options) => {
  parkReveal(root);
  releaseReveal(root, options);
};

const onScroll = rafThrottle(sweep);

let booted = false;
const bootReveal = () => {
  if (booted) return
  booted = true;

  clearTimeout(window.revealSafety);

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', sweep, { passive: true });
  window.addEventListener('load', sweep);
  setTimeout(sweep, 700);

  initReveal();

  const mo = new MutationObserver(records => {
    let dirty = false;
    records.forEach(r => {
      r.addedNodes.forEach(n => {
        if (n.nodeType !== Node.ELEMENT_NODE) return
        if (n.hasAttribute('reveal') || n.querySelector('[reveal]')) dirty = true;
      });
    });
    if (dirty) initReveal();
  });
  mo.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', e => replayReveal(e.target));

  document.addEventListener(ThemeEvents.drawerOpen, e => {
    if (e.target?.querySelector?.('[reveal]')) replayReveal(e.target);
  });
};

class SlideshowComponent extends SliderComponent {
  get _modules() {
    return [...super._modules, Autoplay, EffectFade]
  }

  get _swiperConfig() {
    const base = super._swiperConfig;
    if (reducedMotion.matches) {
      return {
        ...base,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: cssTimeMs(this, '--dur-base'),
        autoplay: false,
      }
    }
    const travel = this._config.effect === 'slide' ? '--dur-hero-travel' : '--dur-hero';
    return { ...base, speed: cssTimeMs(this, travel) }
  }

  connectedCallback() {
    super.connectedCallback();
    if (this._keysBound) return
    this._keysBound = true;
    this.addEventListener('keydown', (e) => {
      if (!this.swiper) return
      const rtl = this.swiper.rtlTranslate;
      if (e.key === 'ArrowRight') {
        rtl ? this.swiper.slidePrev() : this.swiper.slideNext();
        e.preventDefault();
      }
      if (e.key === 'ArrowLeft') {
        rtl ? this.swiper.slideNext() : this.swiper.slidePrev();
        e.preventDefault();
      }
    });
  }

  _setup() {
    super._setup();
    if (!this.swiper) return

    this._current = this._activeSlide();
    this.swiper.on('slideChangeTransitionStart', () => {
      const next = this._activeSlide();
      if (!next || next === this._current) return
      this._current?.classList.add('is-leaving');
      this._current = next;
      parkReveal(next);
    });
    this.swiper.on('slideChangeTransitionEnd', () => {
      this.swiper.slides.forEach((s) => s.classList.remove('is-leaving'));
      const landed = this._current;
      if (!landed) return
      requestAnimationFrame(() => releaseReveal(landed));
    });

    this._enter();
  }

  _activeSlide() {
    return this.querySelector('.swiper-slide-active')
  }

  _enter() {
    if (this._entered) return
    this._entered = true;
    if (this._activeSlide()?.querySelector('[instant-copy]')) {
      this.classList.remove('is-preload');
      return
    }

    const release = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.classList.remove('is-preload');
          replayReveal(this._activeSlide());
        })
      );

    const slide = this._activeSlide();
    const img = [...(slide?.querySelectorAll('img') ?? [])].find(
      (i) => getComputedStyle(i).display !== 'none'
    );
    if (!img || img.complete) {
      release();
      return
    }
    img.addEventListener('load', release, { once: true });
    img.addEventListener('error', release, { once: true });
  }
}

customElements.get('slideshow-component') ||
  customElements.define('slideshow-component', SlideshowComponent);

class MulticolumnCarousel extends SliderComponent {
  connectedCallback() {
    this._assignRevealSteps();
    super.connectedCallback();
  }

  _assignRevealSteps() {
    const cols = parseInt(this.getAttribute('reveal-cols'), 10);
    this._queryAll('[slide][reveal]').forEach((card, i) => {
      card.setAttribute('reveal-step', String(cols > 0 ? (i % cols) + 1 : i + 1));
    });
  }

  _buildPagination() {
    const el = this._controls?.querySelector('[slider-pagination]');
    if (el) return el

    const created = document.createElement('div');
    created.className = 'multicolumn__pagination';
    this._paginationEl = created;
    this._swiperContainer.after(created);
    return created
  }
}

customElements.define('multicolumn-carousel', MulticolumnCarousel);

class CollectionListGrid extends SliderComponent {
  connectedCallback() {
    this._assignRevealSteps();
    super.connectedCallback();
  }

  _assignRevealSteps() {
    const cols = parseInt(this.getAttribute('reveal-cols'), 10);
    const grid = cols > 0;
    this._queryAll('[slide][reveal]').forEach((card, i) => {
      const step = grid ? (i % cols) + 1 : i + 1;
      card.setAttribute('reveal-step', String(step));
      if (grid) card.setAttribute('reveal-with', '[collection-list-head]');
    });
  }
}

customElements.define('collection-list-grid', CollectionListGrid);

class CollectionsGrid extends HTMLElement {
  #observer = null

  connectedCallback() {
    this.list = this.querySelector('[load-more-list]');
    if (!this.list) return

    this.#assign();

    this.#observer = new MutationObserver(() => {
      this.#assign();
      initReveal(this.list);
    });
    this.#observer.observe(this.list, { childList: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #assign() {
    const cols = this.#columns();
    Array.from(this.list.children).forEach((card, i) => {
      card.setAttribute('reveal-step', String((i % cols) + 2));
    });
  }

  #columns() {
    const tracks = getComputedStyle(this.list).gridTemplateColumns;
    if (!tracks || tracks === 'none') return 1
    return Math.max(1, tracks.split(' ').length)
  }
}

customElements.define('collections-grid', CollectionsGrid);

function tablistKeydownIndex(event, tabs) {
  const index = tabs.indexOf( (event.target));
  if (index === -1) return -1

  let next = index;

  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = index + 1;
      break
    case 'ArrowLeft':
    case 'ArrowUp':
      next = index - 1;
      break
    case 'Home':
      next = 0;
      break
    case 'End':
      next = tabs.length - 1;
      break
    default:
      return -1
  }

  event.preventDefault();
  return (next + tabs.length) % tabs.length
}


const FILL_BASE = 100;

const placeTravellingFill = (host, fill, active) => {
  const list = fill?.parentElement;
  if (!host || !fill || !list || !active || list.hidden) return

  const listRect = list.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();
  const x = activeRect.left - (listRect.left + list.clientLeft - list.scrollLeft);

  host.style.setProperty('--tab-x', `${x.toFixed(2)}px`);
  host.style.setProperty('--tab-scale', (activeRect.width / FILL_BASE).toFixed(4));
};

const watchTravellingFill = (list, place) => {
  document.fonts?.ready.then(place);

  if (!('ResizeObserver' in window)) {
    window.addEventListener('resize', place, { passive: true });
    return { disconnect: () => window.removeEventListener('resize', place) }
  }

  const observer = new ResizeObserver(place);
  observer.observe(list);
  return observer
};

const pending = new WeakMap();

function release(card) {
  const morph = pending.get(card);
  if (!morph) return
  clearTimeout(morph.timer);
  morph.el.classList.remove('is-morphing');
  morph.el.style.removeProperty('block-size');
  pending.delete(card);
}

function morphHeight(card, change, duration, { target } = {}) {
  if (!card || reducedMotion.matches) {
    change?.();
    return
  }

  const from = card.getBoundingClientRect().height;
  release(card);

  change?.();

  const to = card.getBoundingClientRect().height;
  if (Math.abs(to - from) < 1) return

  const el = target || card;
  const settled = el.getBoundingClientRect().height;
  el.style.blockSize = `${settled + from - to}px`;
  void el.offsetWidth;
  el.classList.add('is-morphing');
  el.style.blockSize = `${settled}px`;

  pending.set(card, { el, timer: setTimeout(() => release(card), duration + 60) });
}

let _uid = 0;

const MORPH_FALLBACK = 240;

class TabGroup extends HTMLElement {
  #uid = ++_uid
  #abortController = null
  #blockSelectController = null
  #initRaf = null
  #resizeObserver = null
  #replayTimer = 0

  connectedCallback() {
    this.#blockSelectController = new AbortController();
    document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), {
      signal: this.#blockSelectController.signal,
    });

    this.querySelectorAll('[tab-panel]').forEach((panel, i) => {
      panel.hidden = i !== 0;
    });
    this.#initRaf = requestAnimationFrame(() => this.#init());
  }

  #init() {
    if (!this.isConnected) return
    this.#abortController = new AbortController();

    const tablist = this.querySelector('[role="tablist"]');
    if (tablist) {
      for (const wrapper of this.querySelectorAll('[tab-trigger]')) {
        const btn = wrapper.querySelector('[role="tab"]');
        if (btn) tablist.appendChild(btn);
        wrapper.remove();
      }
    }

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[tab-panel]'));

    this.#wire();
    this.#placeFill();
    this.dispatchEvent(new TabGroupReadyEvent());

    const { signal } = this.#abortController;

    if (tablist && this.querySelector('[tab-fill]')) {
      this.#resizeObserver = watchTravellingFill(tablist, () => this.#placeFill());
    }

    if (this.tabs.length < 2) {
      if (tablist) tablist.hidden = true;
      return
    }

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.#select(tab), { signal });
      tab.addEventListener('keydown', (event) => this.#keydown(event), { signal });
    }
  }

  #onBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || (!this.contains(target) && !target.contains(this))) return

    if (!this.tabs && this.querySelector('[tab-panel]')) {
      cancelAnimationFrame(this.#initRaf);
      this.#init();
    }
    if (!this.tabs) return

    let panel = target.closest('[tab-panel]');
    if (!panel) {
      const contained = this.panels.filter((candidate) => target.contains(candidate));
      if (contained.length === 1) panel = contained[0];
    }

    const index = panel ? this.panels.indexOf(panel) : -1;
    if (index !== -1) this.#select(this.tabs[index]);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#initRaf);
    clearTimeout(this.#replayTimer);
    this.#abortController?.abort();
    this.#blockSelectController?.abort();
    this.#resizeObserver?.disconnect();
  }

  #replayPanel(panel) {
    clearTimeout(this.#replayTimer);
    this.panels.forEach((p) => p.classList.remove('is-replaying'));
    const items = panel ? [...panel.querySelectorAll('[reveal]')] : [];
    if (!items.length) return

    if (!reducedMotion.matches) panel.classList.add('is-replaying');
    replayReveal(panel, { force: true });
    if (reducedMotion.matches) return

    const last = items.reduce(
      (max, el) => Math.max(max, parseFloat(el.style.getPropertyValue('--reveal-delay')) || 0),
      0
    );
    const duration = cssTimeMs(panel, '--dur-panel', 350);
    this.#replayTimer = setTimeout(() => panel.classList.remove('is-replaying'), last + duration + 80);
  }

  #placeFill() {
    const fill = this.querySelector('[tab-fill]');
    const tab = this.tabs?.find((t) => t.getAttribute('aria-selected') === 'true');
    placeTravellingFill(this, fill, tab);
  }

  #wire() {
    this.tabs.forEach((tab, i) => {
      if (!tab.id) tab.id = `tg-${this.#uid}-tab-${i + 1}`;

      const panelId = tab.getAttribute('aria-controls') || `tg-${this.#uid}-panel-${i + 1}`;
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      tab.tabIndex = i === 0 ? 0 : -1;

      const panel = this.panels[i];
      if (!panel) return

      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      panel.hidden = i !== 0;
    });

    for (let i = this.tabs.length; i < this.panels.length; i++) {
      this.panels[i].hidden = i !== 0;
    }
  }

  #select(tab, focus = false) {
    const index = this.tabs.indexOf(tab);
    if (index === -1) return

    if (tab.getAttribute('aria-selected') === 'true' && !this.panels[index]?.hidden) {
      if (focus) tab.focus();
      return
    }

    for (const [i, t] of this.tabs.entries()) {
      const active = i === index;
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.tabIndex = active ? 0 : -1;
    }

    const incoming = this.panels[index];
    const morphMs = cssTimeMs(document.documentElement, '--dur-base', MORPH_FALLBACK);
    morphHeight(
      this.querySelector('[tab-panels]'),
      () => {
        for (const [i, panel] of this.panels.entries()) panel.hidden = i !== index;
        this.#replayPanel(incoming);
      },
      morphMs,
      { target: incoming }
    );

    this.classList.add('is-armed');
    this.#placeFill();

    const tablist = tab.closest('[role="tablist"]');
    if (tablist) {
      const tabRect = tab.getBoundingClientRect();
      const listRect = tablist.getBoundingClientRect();
      const behavior = reducedMotion.matches ? 'auto' : 'smooth';

      const padInlineStart = parseFloat(getComputedStyle(tablist).paddingLeft) || 0;
      const wanted = tabRect.left - (listRect.left + tablist.clientLeft + padInlineStart);
      const maxScroll = tablist.scrollWidth - tablist.clientWidth - tablist.scrollLeft;
      const delta = Math.min(wanted, maxScroll);
      if (delta !== 0) {
        tablist.scrollBy({ left: delta, behavior });
      }
    }

    if (focus) tab.focus();

    this.dispatchEvent(new TabGroupChangeEvent(index, tab, this.panels[index]));
  }

  #keydown(event) {
    const next = tablistKeydownIndex(event, this.tabs);
    if (next === -1) return
    this.#select(this.tabs[next], true);
  }
}

if (!customElements.get('tab-group')) {
  customElements.define('tab-group', TabGroup);
}

function storeScrollbarWidth() {
  if (getComputedStyle(document.body).overflowY === 'hidden') return
  document.documentElement.style.setProperty(
    '--scrollbar-width',
    `${window.innerWidth - document.documentElement.clientWidth}px`
  );
}

const DRAWER_HOST_SELECTOR = 'drawer-component, quick-view-modal, quiz-result-modal, ugc-lightbox, popup-component, password-modal';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

let scrollLocks = 0;

class DrawerComponent extends HTMLElement {
  #panel = null
  #backdrop = null
  #triggerEl = null
  #returnEl = null
  #abortController = null
  #didLock = false
  #initRaf = null

  get isOpen() {
    return this.hasAttribute('open')
  }

  get #isFlyoutMode() {
    if (!this.hasAttribute('desktop-flyout')) return false
    if (window.matchMedia('(min-width: 1024px)').matches) return true
    return document.querySelector('[header-layout="standard"]') !== null
  }

  get shouldAutoFocus() {
    return true
  }

  connectedCallback() {
    this.#initRaf = requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#initRaf);
    this.#abortController?.abort();
    if (this.isOpen) this.#unlock();
  }

  #init() {
    if (!this.isConnected) return
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#panel = this.querySelector('[drawer-panel]');
    this.#backdrop = this.querySelector('[drawer-backdrop]');

    if (!this.isOpen) {
      this.#panel?.setAttribute('inert', '');
    }

    this.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[drawer-close]');
      if (closeBtn && closeBtn.closest(DRAWER_HOST_SELECTOR) === this) this.close();
    }, { signal });

    this.#backdrop?.addEventListener('click', () => this.close(), { signal });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen && !this.querySelector('drawer-component[open]')) {
        e.preventDefault();
        this.close();
      }
    }, { signal });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && this.isOpen) this.#trapFocus(e);
    }, { signal });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[open-drawer]');
      if (trigger && trigger.getAttribute('open-drawer') === this.id) {
        this.#triggerEl = trigger;
        this.open();
      }
    }, { signal });

    document.addEventListener('click', (e) => {
      const closer = e.target.closest('[close-drawer]');
      if (!closer) return
      const target = closer.getAttribute('close-drawer');
      if (!target || target === this.id) this.close();
    }, { signal });
  }


  open() {
    if (this.isOpen) return

    if (this.id === 'cart-drawer') {
      document.querySelector('quick-view-modal[open]')?.close();
      document.querySelector('ugc-lightbox[open]')?.close();
      document.querySelector("drawer-component[id^='si-drawer-'][open]")?.close();
    }

    const active = document.activeElement;
    this.#returnEl = active && active !== document.body && !this.contains(active) ? active : null;

    this.setAttribute('open', '');
    this.#panel?.removeAttribute('inert');
    this.#lock();
    this.#focusFirst();

    this.dispatchEvent(new DrawerOpenEvent());
  }

  #focusFirst() {
    if (this.#isFlyoutMode || !this.shouldAutoFocus) return

    const attempt = () => {
      const first = this.#panel?.querySelector(FOCUSABLE_SELECTORS);
      const target = first ?? this.querySelector('[drawer-close]') ?? this.#panel;
      target?.focus({ preventScroll: true });
      return this.#panel?.contains(document.activeElement) ?? false
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (attempt()) return
        let done = false;
        const retry = () => {
          if (done) return
          done = true;
          this.#panel?.removeEventListener('transitionend', retry);
          if (this.isOpen) attempt();
        };
        this.#panel?.addEventListener('transitionend', retry);
        setTimeout(retry, 400);
      });
    });
  }

  close() {
    if (!this.isOpen) return
    this.removeAttribute('open');
    this.#panel?.setAttribute('inert', '');
    this.#unlock();
    const returnTo = this.#triggerEl ?? this.#returnEl;
    if (returnTo?.isConnected) returnTo.focus();
    this.#triggerEl = null;
    this.#returnEl = null;

    this.dispatchEvent(new DrawerCloseEvent());
  }


  #trapFocus(e) {
    if (this.#isFlyoutMode) return

    const focusable = Array.from(
      this.#panel?.querySelectorAll(FOCUSABLE_SELECTORS) ?? []
    ).filter((el) => !el.closest('[inert], [hidden]'));
    if (focusable.length < 2) return

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  #lock() {
    if (this.hasAttribute('contained')) return
    this.#didLock = true;
    storeScrollbarWidth();
    scrollLocks++;
    document.body.classList.add('drawer-open');
  }

  #unlock() {
    if (this.hasAttribute('contained')) return
    if (!this.#didLock) return
    this.#didLock = false;
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('drawer-open');
  }
}

if (!customElements.get('drawer-component')) {
  customElements.define('drawer-component', DrawerComponent);
}


function parseHTML(text) {
  return new DOMParser().parseFromString(text, 'text/html')
}

async function fetchSectionDoc(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err
  }
  return parseHTML(await res.text())
}

function replaceInner(doc, root, selector) {
  const next = doc.querySelector(selector);
  const live = root.querySelector(selector);
  if (next && live) live.innerHTML = next.innerHTML;
}

function replaceOuter(doc, root, selector) {
  const next = doc.querySelector(selector);
  const live = root.querySelector(selector);
  if (next && live) live.replaceWith(next);
}

function replaceText(doc, root, selector, attrs = []) {
  const next = doc.querySelector(selector);
  const live = root.querySelector(selector);
  if (!next || !live) return
  live.textContent = next.textContent;
  for (const name of attrs) {
    const value = next.getAttribute(name);
    if (value == null) live.removeAttribute(name);
    else live.setAttribute(name, value);
  }
}

class QuickViewModal extends DrawerComponent {
  #controller = null
  #fetch = null
  #body = null
  #triggerEl = null
  #loadingTrigger = null
  #sectionId = null
  #clearTimer = null
  #editorPreview = ''

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#body = this.querySelector('[drawer-body]');
    this.#sectionId = this.getAttribute('section-id');

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[quick-view-trigger]');
      if (trigger) {
        this.#triggerEl = trigger;
        this.openForProduct(trigger.getAttribute('quick-view-trigger'));
      }
    }, { signal });

    if (window.Shopify?.designMode) {
      this.#editorPreview = this.#body?.innerHTML ?? '';
      document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
      document.addEventListener('shopify:section:load', this.#onSectionSelect, { signal });
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#fetch?.abort();
    this.#cancelClear();
  }

  close() {
    this.#fetch?.abort();
    this.#clearTriggerLoading();
    super.close();
    this.#triggerEl?.focus();
    this.#triggerEl = null;
    this.#scheduleClear();
  }

  async openForProduct(productUrl) {
    const base = (productUrl || '').split('?')[0];
    if (!base || !this.#body || !this.#sectionId) return

    this.#fetch?.abort();
    this.#clearTriggerLoading();
    this.#cancelClear();
    this.#body.innerHTML = '';
    const controller = new AbortController();
    this.#fetch = controller;

    this.classList.add('is-loading');
    this.#loadingTrigger = this.#triggerEl;
    this.#loadingTrigger?.classList.add('is-loading');
    this.#loadingTrigger?.setAttribute('aria-busy', 'true');

    try {
      const url = `${base}?section_id=${this.#sectionId}`;
      const doc = await fetchSectionDoc(url, { signal: controller.signal });
      if (this.#fetch !== controller) return
      const content =
        doc.querySelector('[quick-view-content]') ??
        doc.querySelector('template[quick-view-source]')?.content.querySelector('[quick-view-content]');
      if (!content) throw new Error('quick-view content not found in response')
      this.#body.innerHTML = content.outerHTML;
      super.open();
    } catch (err) {
      if (err.name !== 'AbortError') this.#body.innerHTML = '';
    } finally {
      if (!controller.signal.aborted) {
        this.classList.remove('is-loading');
        this.#clearTriggerLoading();
      }
      if (this.#fetch === controller) this.#fetch = null;
    }
  }

  #CLEAR_DELAY = 300

  #scheduleClear() {
    this.#cancelClear();
    this.#clearTimer = setTimeout(() => {
      this.#clearTimer = null;
      if (!this.hasAttribute('open') && !this.#fetch) this.#body.innerHTML = '';
    }, this.#CLEAR_DELAY);
  }

  #cancelClear() {
    if (this.#clearTimer) clearTimeout(this.#clearTimer);
    this.#clearTimer = null;
  }

  #clearTriggerLoading() {
    this.#loadingTrigger?.classList.remove('is-loading');
    this.#loadingTrigger?.removeAttribute('aria-busy');
    this.#loadingTrigger = null;
  }

  #onSectionSelect = (event) => {
    if (event.target.contains(this)) this.#openPreview();
  }

  #onBlockSelect = (event) => {
    if (this.contains(event.target)) this.#openPreview();
  }

  #openPreview() {
    if (this.hasAttribute('open') && this.#body.childElementCount) return
    this.#fetch?.abort();
    this.#cancelClear();
    this.#body.innerHTML = this.#editorPreview;
    super.open();
  }

  #onSectionDeselect = (event) => {
    if (event.target.contains(this)) this.close();
  }
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}

const rootUrl = () => window.Shopify?.routes?.root ?? '/';


async function refreshCartAfterFailedAdd(sourceId, { message = '', open = true } = {}) {
  const drawer = document.querySelector('cart-drawer') ?? document.querySelector('cart-items');
  let refreshed = false;
  if (typeof drawer?._refresh === 'function') {
    try {
      await drawer._refresh();
      refreshed = true;
    } catch {
    }
  }

  document.dispatchEvent(new CartUpdateEvent(null, sourceId, { source: 'cart-add-error' }));

  announce(message);

  const panel = drawer?.closest('drawer-component');
  if (open && refreshed && panel && !drawer.classList.contains('is-empty') && !panel.isOpen) panel.open();
}

function announce(message) {
  const live = document.querySelector('[cart-error-live-region]');
  if (!live || !message) return
  live.textContent = '';
  requestAnimationFrame(() => {
    live.textContent = message;
  });
}

function setLabelText(label, text) {
  if (!label || text == null) return
  label.textContent = text;
  label.dataset.label = text;
}

function setButtonLabel(button, text, selector = '[btn-label]') {
  setLabelText(button?.querySelector(selector), text);
}

function themeString(path, fallback = '') {
  let node = window.themeStrings;
  for (const key of path.split('.')) {
    if (node == null) return fallback
    node = node[key];
  }
  return node ?? fallback
}



const LIBRARY_SPECIFIER = '@shopify/events';

let libraryPromise = null;

function loadStandardEvents() {
  libraryPromise ??= import( LIBRARY_SPECIFIER).catch(() => null);
  return libraryPromise
}

function createDeferred() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  promise.catch(() => {});
  return { promise, resolve, reject }
}

async function dispatchStandardEvent(target, className, payload) {
  const library = await loadStandardEvents();
  const StandardEvent = library?.[className];
  if (!StandardEvent) return
  try {
    ;(target ?? document).dispatchEvent(new StandardEvent(payload));
  } catch (error) {
    console.warn(`[standard-events] ${className} not dispatched`, error);
  }
}

async function toCartSummary(ajaxCart) {
  if (!ajaxCart) return null
  const library = await loadStandardEvents();
  if (!library) return null
  try {
    return library.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
  } catch {
    return null
  }
}

async function fetchCartSummary() {
  const library = await loadStandardEvents();
  if (!library) return null
  try {
    const root = rootUrl();
    const response = await fetch(`${root}cart.js`);
    if (!response.ok) return null
    return toCartSummary(await response.json())
  } catch {
    return null
  }
}

function dispatchCartLinesUpdate(target, { action, context, lines, detail }) {
  const deferred = createDeferred();
  dispatchStandardEvent(target, 'CartLinesUpdateEvent', {
    action,
    context,
    lines,
    promise: deferred.promise,
    detail,
  });
  return deferred
}

function dispatchCartNoteUpdate(target, { context, note }) {
  const deferred = createDeferred();
  dispatchStandardEvent(target, 'CartNoteUpdateEvent', {
    context,
    note,
    promise: deferred.promise,
  });
  return deferred
}

function dispatchCartDiscountUpdate(target, { codes }) {
  const deferred = createDeferred();
  dispatchStandardEvent(target, 'CartDiscountUpdateEvent', {
    discountCodes: codes.map((code) => ({ code })),
    promise: deferred.promise,
  });
  return deferred
}

function dispatchCartError(target, { error, code = 'INVALID' }) {
  dispatchStandardEvent(target, 'CartErrorEvent', { error, code });
}

function dispatchProductSelect(target, { product, selectedOptions }) {
  const deferred = createDeferred();
  dispatchStandardEvent(target, 'ProductSelectEvent', {
    product,
    selectedOptions,
    promise: deferred.promise,
  });
  return deferred
}

function dispatchCollectionUpdate(target, { collection, params }) {
  const deferred = createDeferred();
  loadStandardEvents().then((library) => {
    if (!library) return
    const { CollectionUpdateEvent } = library;
    try {
      target.dispatchEvent(
        new CollectionUpdateEvent({
          collection,
          productFilters: CollectionUpdateEvent.parseProductFilters(params),
          sortKey: CollectionUpdateEvent.getSortKey(params),
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[standard-events] CollectionUpdateEvent not dispatched', error);
    }
  });
  return deferred
}

function dispatchSearchUpdate(target, { query, params }) {
  const deferred = createDeferred();
  loadStandardEvents().then((library) => {
    if (!library) return
    const { SearchUpdateEvent } = library;
    try {
      target.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query,
            productFilters: params ? SearchUpdateEvent.parseProductFilters(params) : undefined,
            sortKey: params ? SearchUpdateEvent.getSortKey(params) : undefined,
          },
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[standard-events] SearchUpdateEvent not dispatched', error);
    }
  });
  return deferred
}

async function dispatchCartView(target, context = 'dialog') {
  const cart = await fetchCartSummary();
  await dispatchStandardEvent(target, 'CartViewEvent', { context, cart });
}

function dispatchPageView() {
  dispatchStandardEvent(document, 'PageViewEvent', {
    page: {
      template: document.body.dataset.template || '',
      title: document.title,
      url: window.location.href,
    },
  });
}


const VIEW_EVENT_ATTR = 'view-event-payload';

const armedElements = new WeakSet();

let viewObserver = null;

function readViewPayload(element) {
  const raw =
    element.getAttribute(VIEW_EVENT_ATTR)?.trim() ||
    element.querySelector(`script[type="application/json"][data-${VIEW_EVENT_ATTR}]`)?.textContent?.trim();
  if (!raw) return null
  try {
    const payload = JSON.parse(raw);
    return payload && typeof payload === 'object' ? payload : null
  } catch (error) {
    console.warn('[standard-events] could not parse view-event-payload', error);
    return null
  }
}

function dispatchViewEvent(library, element) {
  if (!element.isConnected) return
  const payload = readViewPayload(element);
  if (!payload) return

  try {
    if ('collection' in payload) {
      element.dispatchEvent(new library.CollectionViewEvent(payload));
      return
    }
    const withContext = { ...payload };
    withContext.context ??= element.closest('dialog') ? 'dialog' : 'page';

    if ('product' in withContext) element.dispatchEvent(new library.ProductViewEvent(withContext));
    else if ('cart' in withContext) element.dispatchEvent(new library.CartViewEvent(withContext));
  } catch (error) {
    console.warn('[standard-events] view event not dispatched', error);
  }
}

function armViewEvent(library, element) {
  if (armedElements.has(element)) return
  armedElements.add(element);

  const trigger = element.getAttribute('view-event-trigger') || 'connect';
  if (trigger === 'manual') return

  if (trigger === 'intersect') {
    viewObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          viewObserver.unobserve(entry.target);
          dispatchViewEvent(library, entry.target);
        }
      },
      { threshold: 0.5 }
    );
    viewObserver.observe(element);
    return
  }

  setTimeout(() => dispatchViewEvent(library, element));
}

async function upgradeViewEvents() {
  const library = await loadStandardEvents();
  if (!library) return

  const scan = (root) => {
    if (root.nodeType !== Node.ELEMENT_NODE) return
    if (root.hasAttribute(VIEW_EVENT_ATTR)) armViewEvent(library, root);
    root.querySelectorAll(`[${VIEW_EVENT_ATTR}]`).forEach((element) => armViewEvent(library, element));
  };

  scan(document.body);

  new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach(scan);
  }).observe(document.body, { childList: true, subtree: true });
}

class ProductCard extends HTMLElement {
  #abortController = null

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#setupCart(this.#abortController.signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #setupCart(signal) {
    this.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = e.submitter;
      if (!btn) return

      const formData = new FormData(e.target);
      const variantId = formData.get('id');
      if (!variantId) return

      const labelEl = btn.querySelector('.btn__label');
      const originalLabel = labelEl?.textContent ?? '';
      const successLabel = themeString('card.addedToCart');

      btn.setAttribute('disabled', '');
      btn.classList.add('is-loading');
      let added = false;

      const linesDeferred = dispatchCartLinesUpdate(this, {
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(variantId),
            quantity: Number(formData.get('quantity') ?? 1) || 1,
          },
        ],
      });

      try {
        const drawerSection = document.querySelector('cart-drawer')?.getAttribute('section-id');
        const body = { items: [{ id: Number(variantId), quantity: Number(formData.get('quantity') ?? 1) }] };
        if (drawerSection) {
          body.sections = drawerSection;
          body.sections_url = window.location.pathname;
        }

        const resp = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (resp.ok) {
          added = true;
          const result = await resp.json();
          document.dispatchEvent(
            new CartAddEvent(null, e.target.id, { source: 'product-card', variantId, sections: result.sections ?? null })
          );
          fetchCartSummary().then((cart) => linesDeferred.resolve({ cart }));
        } else {
          if (resp.status === 422) {
            const { description = '' } = await resp.json().catch(() => ({}));
            refreshCartAfterFailedAdd(e.target.id, { message: description });
          }
          dispatchCartError(this, { error: 'Add to cart failed' });
          linesDeferred.reject(new Error(`cart/add.js responded ${resp.status}`));
        }
      } catch (error) {
        dispatchCartError(this, { error: error?.message || 'Add to cart failed' });
        linesDeferred.reject(error);
      } finally {
        btn.classList.remove('is-loading');
        if (added) {
          btn.setAttribute('added', '');
          if (successLabel) setLabelText(labelEl, successLabel);
          setTimeout(() => {
            btn.removeAttribute('added');
            btn.removeAttribute('disabled');
            if (successLabel) setLabelText(labelEl, originalLabel);
          }, 2000);
        } else {
          btn.removeAttribute('disabled');
        }
      }
    }, { signal });
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}


const CARD_SECTION = 'section-rendering-product-card';
const SHEET_ID = 'pcard-variant-sheet';
const DESKTOP_MQ = '(min-width: 768px)';

class VariantOptions extends HTMLElement {
  #controller = null
  #onKey = null
  #fetch = null
  #sheet = null
  #sheetAbort = null
  #releaseFn = null
  #owningSheet = false

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.card = this.closest('product-card');
    this.toggle = this.card?.querySelector('[variant-toggle]');
    this.popup = this.querySelector('[variant-popup]');
    this.idInput = this.querySelector('[variant-id]');
    this.groups = [...this.querySelectorAll('[variant-group]')];
    this.priceEl = this.querySelector('[variant-price]');
    this.compareEl = this.querySelector('[variant-compare]');
    this.saleEl = this.querySelector('[variant-sale]');
    this.unitEl = this.querySelector('[variant-unit]');
    this.atc = this.querySelector('[variant-atc]');
    this.atcLabel = this.#renderedLabel(this.atc);
    this.productUrl = (this.getAttribute('product-url') || '').split('?')[0];

    this.cardImage = this.card?.querySelector('[card-media] img');
    this.cardPriceRow = this.card?.querySelector('[price-row]');
    this.cardAtc = this.card?.querySelector('[card-atc]');
    this.cardAtcId = this.card?.querySelector('[card-atc-id]');
    this.cardAtcBuyable = Boolean(this.cardAtc) && !this.cardAtc.disabled;
    this.cardAtcLabel = this.#renderedLabel(this.cardAtc);
    this.cardAtcLabelCompact = this.#renderedLabel(this.cardAtc, true);

    this.toggle?.addEventListener('click', () => this.#toggle(), { signal });

    this.addEventListener('click', (e) => {
      if (e.target.closest('[variant-close]')) this.close();
    }, { signal });

    this.querySelectorAll('[variant-input]').forEach((input) => {
      input.addEventListener('change', (e) => this.#onSelect(e), { signal });
    });

    this.card?.addEventListener('change', (e) => {
      const swatch = e.target.closest('[swatch-input]');
      if (!swatch) return
      const radio = this.querySelector(
        `[variant-input][option-value-id="${swatch.getAttribute('option-value-id')}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, { signal });

    this.card?.addEventListener('click', (e) => {
      if (e.target.closest('[swatch-more]')) this.#reveal();
    }, { signal });

    this.querySelector('form')?.addEventListener('submit', () => this.#dismiss(), { signal });

    this.#onKey = (e) => {
      if (e.key === 'Escape' && this.open) this.close();
    };
    document.addEventListener('keydown', this.#onKey, { signal });

    document.addEventListener('click', (e) => {
      if (this.open && !this.card?.contains(e.target)) this.close();
    }, { signal });
  }

  #renderedLabel(button, compact = false) {
    const selector = compact ? '[btn-label-compact]' : '[btn-label]';
    if (compact && !button?.querySelector(selector)) return null
    if (!button || button.disabled) return themeString('card.addToCart')
    return button.querySelector(selector)?.textContent ?? themeString('card.addToCart')
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#fetch?.abort();
    document.body.classList.remove('variant-options-open');
    this.#detachSheet();
  }

  get open() {
    return this.hasAttribute('open')
  }

  get #isMobile() {
    return !window.matchMedia(DESKTOP_MQ).matches
  }

  #toggle() {
    const isOpen = this.#isMobile ? this.#owningSheet : this.open;
    isOpen ? this.#dismiss() : this.#reveal();
  }

  #reveal() {
    this.#isMobile ? this.#openSheet() : this.show();
  }

  #dismiss() {
    if (this.#isMobile && this.#owningSheet) this.#closeSheet();
    else this.close();
  }


  show() {
    this.popup?.removeAttribute('hidden');
    void this.popup?.offsetHeight;
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('variant-options-open');
  }

  close() {
    if (!this.open) return
    this.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('variant-options-open');
    setTimeout(() => {
      if (!this.open) this.popup?.setAttribute('hidden', '');
    }, 300);
  }


  #openSheet() {
    const sheet = document.getElementById(SHEET_ID);
    if (!sheet) {
      this.show();
      return
    }
    sheet.__release?.();

    this.#sheet = sheet;
    this.#owningSheet = true;
    this.#releaseFn = () => this.#releaseSheet();
    sheet.__release = this.#releaseFn;
    sheet.addEventListener(ThemeEvents.drawerClose, this.#releaseFn);

    this.#sheetAbort = new AbortController();
    const { signal } = this.#sheetAbort;
    const body = sheet.querySelector('[drawer-body]');
    body?.addEventListener('change', (e) => {
      const input = e.target.closest('[variant-input]');
      if (input) this.#selectByOptionId(input.getAttribute('option-value-id'));
    }, { signal });
    body?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#addFromSheet();
    }, { signal });
    body?.addEventListener('click', (e) => {
      if (e.target.closest('[variant-close]')) this.#closeSheet();
    }, { signal });

    this.#renderSheet();
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('variant-options-open');
    sheet.open();
  }

  #renderSheet() {
    const body = this.#sheet?.querySelector('[drawer-body]');
    const panel = this.querySelector('[variant-panel]');
    if (!body || !panel) return

    const clone = panel.cloneNode(true);
    if (clone.id) clone.id = `${clone.id}-sheet`;
    this.#syncCloneChecked(panel, clone);
    body.replaceChildren(clone);
  }

  #refreshSheet() {
    if (this.#owningSheet) this.#renderSheet();
  }

  #closeSheet() {
    this.#sheet?.close();
  }

  #releaseSheet() {
    const sheet = this.#sheet;
    const releaseFn = this.#releaseFn;
    this.#detachSheet();
    if (sheet) {
      sheet.querySelector('[drawer-body]')?.replaceChildren();
      if (sheet.__release === releaseFn) sheet.__release = null;
    }
    document.body.classList.remove('variant-options-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  #detachSheet() {
    if (this.#sheet && this.#releaseFn) {
      this.#sheet.removeEventListener(ThemeEvents.drawerClose, this.#releaseFn);
    }
    this.#sheetAbort?.abort();
    this.#sheetAbort = null;
    this.#releaseFn = null;
    this.#owningSheet = false;
    this.#sheet = null;
  }

  #syncCloneChecked(source, clone) {
    const srcInputs = source.querySelectorAll('[variant-input]');
    const cloneInputs = clone.querySelectorAll('[variant-input]');
    srcInputs.forEach((input, i) => {
      if (cloneInputs[i]) cloneInputs[i].checked = input.checked;
    });
  }

  #selectByOptionId(id) {
    const radio = this.querySelector(`[variant-input][option-value-id="${id}"]`);
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #addFromSheet() {
    const form = this.querySelector('form');
    const atc = this.querySelector('[variant-atc]');
    if (!form || !atc || atc.disabled) return
    this.#closeSheet();
    form.requestSubmit(atc);
  }

  #selectedValues() {
    return this.groups.map((group) => group.querySelector('[variant-input]:checked')?.value ?? null)
  }

  #onSelect(event) {
    const selected = this.#selectedValues();

    this.groups.forEach((group, i) => {
      const valueEl = group.querySelector('[variant-value]');
      if (valueEl) valueEl.textContent = selected[i] ?? '';
    });

    const changedId = event.target.getAttribute('option-value-id');
    if (changedId) {
      const cardSwatch = this.card?.querySelector(`[swatch-input][option-value-id="${changedId}"]`);
      if (cardSwatch) cardSwatch.checked = true;
    }

    const optionValueIds = this.groups
      .map((group) => group.querySelector('[variant-input]:checked')?.getAttribute('option-value-id'))
      .filter(Boolean);
    const connectedUrl = event.target.getAttribute('connected-product-url') || '';
    this.#fetchVariant(optionValueIds, connectedUrl);
  }

  async #fetchVariant(optionValueIds, connectedUrl) {
    const base = (connectedUrl || this.productUrl).split('?')[0];
    if (!base || !optionValueIds.length) return

    this.#fetch?.abort();
    const controller = new AbortController();
    this.#fetch = controller;

    this.popup?.classList.add('is-loading');
    if (this.atc) this.atc.disabled = true;
    if (this.cardAtc) this.cardAtc.disabled = true;

    try {
      const url = `${base}?section_id=${CARD_SECTION}&option_values=${optionValueIds.join(',')}`;
      const doc = await fetchSectionDoc(url, { signal: controller.signal });
      const newCard = doc.querySelector('product-card');
      if (!newCard) return

      const productChanged = Boolean(connectedUrl) && connectedUrl.split('?')[0] !== this.productUrl;
      if (productChanged) this.#replaceCard(newCard, connectedUrl);
      else {
        this.#applyUpdate(newCard);
        this.#refreshSheet();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        if (this.atc) this.atc.disabled = false;
        if (this.cardAtc && this.cardAtcBuyable) this.cardAtc.disabled = false;
      }
    } finally {
      if (!controller.signal.aborted) this.popup?.classList.remove('is-loading');
    }
  }

  #updatePillAvailability(newCard) {
    newCard.querySelectorAll('[variant-input]').forEach((freshInput) => {
      const id = freshInput.getAttribute('option-value-id');
      if (!id) return
      const liveInput = this.querySelector(`[variant-input][option-value-id="${id}"]`);
      if (!liveInput) return

      const unavailable = freshInput.hasAttribute('aria-disabled');
      if (unavailable) liveInput.setAttribute('aria-disabled', 'true');
      else liveInput.removeAttribute('aria-disabled');

      const freshVisual = freshInput.nextElementSibling;
      const liveVisual = liveInput.nextElementSibling;
      if (freshVisual && liveVisual) liveVisual.replaceWith(freshVisual.cloneNode(true));
    });
  }

  #applyUpdate(newCard) {
    this.#updatePillAvailability(newCard);

    const newImage = newCard.querySelector('[card-media] img');
    if (this.cardImage && newImage) {
      const srcset = newImage.getAttribute('srcset');
      srcset ? this.cardImage.setAttribute('srcset', srcset) : this.cardImage.removeAttribute('srcset');
      const src = newImage.getAttribute('src');
      if (src) this.cardImage.setAttribute('src', src);
    }

    const newPriceRow = newCard.querySelector('[price-row]');
    if (this.cardPriceRow && newPriceRow) {
      this.cardPriceRow.replaceWith(newPriceRow);
      this.cardPriceRow = newPriceRow;
    }

    const newUnit = newCard.querySelector('[price-unit]');
    const liveUnit = this.card?.querySelector('[price-unit]');
    if (newUnit && liveUnit) liveUnit.replaceWith(newUnit);
    else if (newUnit) this.cardPriceRow?.after(newUnit);
    else liveUnit?.remove();

    const liveSwatchList = this.card?.querySelector('[swatch-list]');
    const newSwatchList = newCard.querySelector('[swatch-list]');
    if (liveSwatchList && newSwatchList) liveSwatchList.innerHTML = newSwatchList.innerHTML;

    const newIdInput = newCard.querySelector('[variant-id]');
    if (this.idInput && newIdInput) {
      this.idInput.value = newIdInput.value;
      if (newIdInput.dataset.variantPrice != null) this.idInput.dataset.variantPrice = newIdInput.dataset.variantPrice;
      if (newIdInput.dataset.variantOptions != null) this.idInput.dataset.variantOptions = newIdInput.dataset.variantOptions;
    }

    const newPrice = newCard.querySelector('[variant-price]');
    if (this.priceEl && newPrice) this.priceEl.textContent = newPrice.textContent;

    const newCompare = newCard.querySelector('[variant-compare]');
    if (this.compareEl) {
      if (newCompare && !newCompare.hasAttribute('hidden')) {
        this.compareEl.textContent = newCompare.textContent;
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }

    const newSale = newCard.querySelector('[variant-sale]');
    if (this.saleEl) {
      if (newSale && !newSale.hasAttribute('hidden')) {
        this.saleEl.textContent = newSale.textContent;
        this.saleEl.hidden = false;
      } else {
        this.saleEl.hidden = true;
      }
    }

    const newVariantUnit = newCard.querySelector('[variant-unit]');
    if (this.unitEl) {
      if (newVariantUnit && !newVariantUnit.hasAttribute('hidden')) {
        this.unitEl.textContent = newVariantUnit.textContent;
        this.unitEl.hidden = false;
      } else {
        this.unitEl.hidden = true;
      }
    }

    const newAtc = newCard.querySelector('[variant-atc]');
    const soldOut = Boolean(newAtc?.hasAttribute('disabled'));
    const newAtcLabel = newAtc?.querySelector('[btn-label]')?.textContent ?? null;

    if (this.atc && newAtc) {
      this.atc.disabled = soldOut;
      setButtonLabel(this.atc, soldOut ? newAtcLabel : this.atcLabel);
    }

    const newAtcLabelCompact = newAtcLabel === themeString('card.unavailable')
      ? newAtcLabel
      : themeString('card.outOfStockCompact');
    if (this.cardAtcId && newIdInput) this.cardAtcId.value = newIdInput.value;
    if (this.cardAtc && newAtc) {
      if (this.cardAtcBuyable) {
        this.cardAtc.disabled = soldOut;
        setButtonLabel(this.cardAtc, soldOut ? newAtcLabel : this.cardAtcLabel);
        setButtonLabel(this.cardAtc, soldOut ? newAtcLabelCompact : this.cardAtcLabelCompact, '[btn-label-compact]');
      } else {
        setButtonLabel(this.cardAtc, newAtcLabel);
        setButtonLabel(this.cardAtc, newAtcLabelCompact, '[btn-label-compact]');
      }
    }

    this.card?.dispatchEvent(new VariantSelectedEvent(this.idInput?.value ?? null));
  }

  #replaceCard(newCard, connectedUrl) {
    const reopen = this.open || this.#owningSheet;
    if (newCard.dataset.productId) this.card.dataset.productId = newCard.dataset.productId;
    this.card.innerHTML = newCard.innerHTML;
    if (reopen) requestAnimationFrame(() => this.card.querySelector('[variant-toggle]')?.click());
  }
}

if (!customElements.get('variant-options')) {
  customElements.define('variant-options', VariantOptions);
}


class FreeShippingNote extends HTMLElement {
  #controller = null

  connectedCallback() {
    this.#controller = new AbortController();
    this.template = this.getAttribute('message-template') || '';
    this.messageEl = this.querySelector('[fs-message]');

    const bar = document.querySelector('free-shipping-bar');
    if (bar) this.#apply(bar.getAttribute('amount') || '', bar.hasAttribute('qualified'));

    document.addEventListener(ThemeEvents.freeShippingUpdate, this.#update, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #update = (event) => {
    const { amount = '', qualified = false } = event.detail || {};
    this.#apply(amount, qualified);
  }

  #apply(amount, qualified) {
    if (!this.messageEl) return
    if (qualified) {
      this.messageEl.textContent = '';
      this.setAttribute('hidden', '');
      return
    }
    this.messageEl.textContent = this.template.replace('[amount]', amount);
    this.removeAttribute('hidden');
  }
}

if (!customElements.get('free-shipping-note')) {
  customElements.define('free-shipping-note', FreeShippingNote);
}

const CURRENCY_DECIMALS = {
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
  PYG: 0, RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
};

const currencyDecimals = (currency) => CURRENCY_DECIMALS[currency?.toUpperCase()] ?? 2;

function parseMoney(value, currency) {
  const groups = String(value ?? '').match(/\d+/g);
  if (!groups) return null
  const decimals = currencyDecimals(currency);
  const hasFraction = decimals > 0 && groups.length > 1 && groups[groups.length - 1].length <= decimals;
  const fraction = hasFraction ? groups.pop() : '';
  const sign = /^\s*-/.test(value) ? -1 : 1;
  return sign * Number(groups.join('') + fraction.padEnd(decimals, '0'))
}

function formatMoney(cents, format, currency) {
  const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const fmt = format || '${{amount}}';
  const decimals = currencyDecimals(currency);

  const number = (precision, thousands, decimal) => {
    precision = isNaN(precision) ? 2 : precision;
    thousands = thousands ?? ',';
    decimal = decimal ?? '.';
    const n = (value / 10 ** decimals).toFixed(precision);
    const parts = n.split('.');
    const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const decPart = parts[1] ? decimal + parts[1] : '';
    return intPart + decPart
  };

  let out = '';
  switch (fmt.match(placeholder)?.[1]) {
    case 'amount': out = number(decimals, ',', '.'); break
    case 'amount_no_decimals': out = number(0, ',', '.'); break
    case 'amount_with_comma_separator': out = number(decimals, '.', ','); break
    case 'amount_no_decimals_with_comma_separator': out = number(0, '.', ','); break
    case 'amount_with_space_separator': out = number(decimals, ' ', ','); break
    case 'amount_no_decimals_with_space_separator': out = number(0, ' ', ''); break
    case 'amount_with_apostrophe_separator': out = number(decimals, "'", '.'); break
    default: out = number(decimals, ',', '.');
  }
  return fmt.replace(placeholder, out)
}


class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('convert')) this.#fill();

    this.dispatchEvent(
      new FreeShippingUpdateEvent({
        amount: this.getAttribute('amount') || '',
        remaining: Number(this.getAttribute('remaining')) || 0,
        qualified: this.hasAttribute('qualified'),
      })
    );
  }

  #fill() {
    const text = this.querySelector('[fs-text]');
    const fill = this.querySelector('[progress-fill]');
    const track = this.querySelector('[progress-track]');
    const threshold = Number(this.getAttribute('threshold'));
    const total = Number(this.getAttribute('cart-total'));
    if (!text || !fill || !track || !(threshold > 0) || !Number.isFinite(total)) return

    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const converted = Math.round(threshold * rate);
    const remaining = Math.max(0, converted - total);
    const qualified = remaining === 0;
    const amount = formatMoney(remaining, this.getAttribute('money-format'));
    const pct = Math.min(100, Math.round((total * 100) / converted));

    const template = this.getAttribute(qualified ? 'success-template' : 'progress-template') || '';
    text.innerHTML = template.split('{amount}').join(amount);
    fill.style.setProperty('--progress', `${pct}%`);
    track.setAttribute('aria-valuenow', pct);

    this.toggleAttribute('qualified', qualified);
    this.setAttribute('amount', amount);
    this.setAttribute('remaining', String(remaining));
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

function stepQuantity(input, delta) {
  const parse = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n
  };
  const current = parse(input.value, 1);
  const min = parse(input.min, 1);
  const max = parse(input.max, Infinity);

  const next = Math.min(max, Math.max(min, current + delta));
  input.value = next;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  return next
}

function playLineExit(row) {
  if (!row || reducedMotion.matches) return null

  const sibling = row.nextElementSibling;
  const rows = sibling?.hasAttribute?.('gift-wrap-group') ? [row, sibling] : [row];
  const out = cssTimeMs(row, '--dur-fast', 180);
  const collapse = cssTimeMs(row, '--dur-panel', 350);

  for (const el of rows) el.classList.add('is-going');

  const done = new Promise((resolve) => {
    setTimeout(() => {
      for (const el of rows) el.style.blockSize = `${el.getBoundingClientRect().height}px`;
      void row.offsetHeight;
      for (const el of rows) el.classList.add('is-collapsing');
      setTimeout(resolve, collapse);
    }, out);
  });

  return { done, rows }
}

class CartItems extends HTMLElement {
  #abortController = null
  #noteTimer = null
  #discountBusy = false
  #lineExit = null

  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    this.cartUrl = this.getAttribute('cart-url') || '/cart';
    this.#abortController = new AbortController();
    this.#bind(this.#abortController.signal);
    this._populateProvinces();
    this._syncCheckoutButtons();
    if (this._playsBarOnConnect) this._playShippingBar();
  }

  get _playsBarOnConnect() {
    return true
  }

  _playShippingBar() {
    const fill = this.querySelector('[progress-fill]');
    if (!fill) return
    fill.classList.add('is-parked');
    void fill.offsetWidth;
    fill.classList.remove('is-parked');
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    clearTimeout(this.#noteTimer);
  }

  #bind(signal) {
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    window.addEventListener('pageshow', () => this._syncCheckoutButtons(), { signal });

    this.addEventListener('click', this.#onBreakdownClick, { signal });
    this.addEventListener('keydown', this.#onBreakdownKeydown, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener(ThemeEvents.drawerOpen, () => this.#closeBreakdown(), { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.addEventListener('change', this.#onGiftWrapToggle, { signal });
    this.addEventListener('click', this.#onGiftWrapToggleClick, { signal, capture: true });
  }

  get #root() {
    return rootUrl()
  }

  get _eventContext() {
    return 'cart'
  }

  _readTermsChecked() {
    return this.querySelector('[terms-checkbox]')?.checked === true
  }

  _restoreTermsChecked(checked) {
    if (!checked) return
    const box = this.querySelector('[terms-checkbox]');
    if (box) box.checked = true;
  }

  _syncCheckoutButtons() {
    const box = this.querySelector('[terms-checkbox]');
    const accepted = !box || box.checked;
    this.querySelectorAll('[checkout-button]').forEach((btn) => {
      btn.disabled = !accepted;
    });
  }

  #onClick = (event) => {
    const minus = event.target.closest('[quantity-minus]');
    const plus = event.target.closest('[quantity-plus]');
    const remove = event.target.closest('[line-remove]');
    const calcShipping = event.target.closest('[shipping-calculate]');
    const removeDiscount = event.target.closest('[discount-remove]');
    const applyDiscount = event.target.closest('[discount-form] button');

    if (removeDiscount) {
      event.preventDefault();
      this.#removeDiscount(removeDiscount.getAttribute('discount-remove'));
      return
    }

    if (applyDiscount) {
      event.preventDefault();
      this.#applyDiscount();
      return
    }

    if (minus || plus) {
      const wrapper = event.target.closest('[quantity-wrapper]');
      const input = wrapper?.querySelector('[quantity-input]');
      if (!input) return
      const next = stepQuantity(input, plus ? 1 : -1);
      this.#updateQuantity(this.#lineKey(event.target), next);
      return
    }

    if (remove) {
      event.preventDefault();
      this.#updateQuantity(this.#lineKey(remove), 0);
      return
    }

    if (calcShipping) {
      event.preventDefault();
      this.#estimateShipping(calcShipping);
    }
  }

  #onChange = (event) => {
    if (event.target.matches('[terms-checkbox]')) {
      this._syncCheckoutButtons();
      return
    }

    if (event.target.matches('[quantity-input]')) {
      const value = Math.max(0, parseInt(event.target.value, 10) || 0);
      this.#updateQuantity(this.#lineKey(event.target), value);
      return
    }

    if (event.target.matches('[shipping-country]')) {
      this._populateProvinces();
      return
    }

    if (event.target.matches('[name="note"]')) {
      clearTimeout(this.#noteTimer);
      const note = event.target.value;
      const field = event.target;
      this.#noteTimer = setTimeout(async () => {
        const deferred = dispatchCartNoteUpdate(field, { context: this._eventContext, note });
        try {
          const resp = await fetch(`${this.#root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note }),
          });
          if (!resp.ok) throw new Error(`cart/update.js responded ${resp.status}`)
          deferred.resolve({ cart: await toCartSummary(await resp.json()) });
        } catch (error) {
          deferred.reject(error);
        }
      }, 400);
    }
  }

  #onSubmit = (event) => {
    if (event.submitter?.closest('[discount-form]') || document.activeElement?.closest?.('[discount-form]')) {
      event.preventDefault();
      this.#applyDiscount();
      return
    }

    const terms = this.querySelector('[terms-checkbox]');
    const error = this.querySelector('[terms-error]');
    if (terms && !terms.checked) {
      event.preventDefault();
      error?.removeAttribute('hidden');
      terms.focus();
    } else {
      error?.setAttribute('hidden', '');
    }
  }

  #onBreakdownClick = (event) => {
    const popover = this.querySelector('[breakdown-popover]');
    if (!popover) return

    const toggle = event.target.closest('[breakdown-toggle]');
    if (toggle) {
      const willOpen = popover.hidden;
      popover.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
      return
    }
    if (event.target.closest('[breakdown-close]')) {
      this.#closeBreakdown();
      return
    }
    if (!popover.hidden && !event.target.closest('[breakdown-popover]')) {
      this.#closeBreakdown();
    }
  }

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.#closeBreakdown();
  }

  #closeBreakdown() {
    const popover = this.querySelector('[breakdown-popover]');
    if (!popover || popover.hidden) return
    popover.hidden = true;
    this.querySelector('[breakdown-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  #onBreakdownKeydown = (event) => {
    if (event.key !== 'Escape') return
    const popover = this.querySelector('[breakdown-popover]');
    if (popover && !popover.hidden) {
      event.stopPropagation();
      this.#closeBreakdown();
    }
  }

  #onGiftWrapToggle = (event) => {
    if (!event.target.matches('[gift-wrap-toggle]')) return
    const group = event.target.closest('[gift-wrap-group]');
    if (!group) return

    if (event.target.checked) {
      const id = Number(group.getAttribute('gift-wrap-add-variant'));
      const parentKey = group.getAttribute('parent-line-key');
      if (!id || !parentKey) return
      this.#addGiftWrap(id, parentKey);
      return
    }

    const key = group.getAttribute('wrap-line-key');
    if (key) this._updateLines({ [key]: 0 });
  }

  async #addGiftWrap(id, parentKey) {
    this.#setLoading(true);
    let linesDeferred = null;
    try {
      const cart = await (await fetch(`${this.#root}cart.js`)).json();
      const parent = cart.items.find((i) => i.key === parentKey);
      if (!parent) return

      const parentItem = {
        id: parent.variant_id,
        quantity: parent.quantity,
        properties: parent.properties || {},
      };
      const sellingPlanId = parent.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) parentItem.selling_plan = sellingPlanId;

      const removed = await fetch(`${this.#root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [parentKey]: 0 } }),
      }).catch(() => null);
      if (!removed?.ok) return

      let added = false;
      try {
        const resp = await fetch(`${this.#root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              parentItem,
              {
                id,
                quantity: parent.quantity,
                parent_id: parent.variant_id,
                properties: { _wrap_for: String(parent.variant_id) },
              },
            ],
          }),
        });
        added = resp.ok;
      } catch {
        added = false;
      }
      if (!added) {
        await fetch(`${this.#root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [parentItem] }),
        }).catch(() => {});
      }

      linesDeferred = added
        ? dispatchCartLinesUpdate(this, {
            action: 'add',
            context: this._eventContext,
            lines: [{ merchandiseId: String(id), quantity: parent.quantity }],
          })
        : null;

      const [, fresh] = await Promise.all([
        this._refresh(),
        fetch(`${this.#root}cart.js`).then((r) => r.json()),
      ]);
      linesDeferred?.resolve({ cart: await toCartSummary(fresh) });
      document.dispatchEvent(new CartUpdateEvent(fresh, this.sectionId, { source: 'cart-items' }));
    } catch (error) {
      linesDeferred?.reject(error);
    } finally {
      this.#setLoading(false);
    }
  }

  #onGiftWrapToggleClick = (event) => {
    if (!event.target.matches?.('[gift-wrap-toggle]')) return
    event.stopPropagation();
  }

  #lineKey(el) {
    return el.closest('[line-item]')?.getAttribute('line-key')
  }

  async #updateQuantity(key, quantity) {
    if (!key) return
    const updates = { [key]: quantity };
    if (quantity === 0) {
      const row = this.querySelector(`[line-item][line-key="${key}"]`);
      const group = row?.nextElementSibling;
      const wrapKey = group?.hasAttribute?.('gift-wrap-group') && group.getAttribute('wrap-line-key');
      if (wrapKey) updates[wrapKey] = 0;
      this.#lineExit = playLineExit(row);
    }
    return this._updateLines(updates)
  }

  async _updateLines(updates) {
    this.#setLoading(true);

    const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
    const deferred = dispatchCartLinesUpdate(this, {
      action: lines.every((line) => line.quantity === 0) ? 'remove' : 'update',
      context: this._eventContext,
      lines,
    });

    try {
      const resp = await fetch(`${this.#root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!resp.ok) throw new Error(`cart/update.js responded ${resp.status}`)

      const cart = await resp.json();
      deferred.resolve({ cart: await toCartSummary(cart) });
      await this.#settleLineExit();
      await this._refresh();
      document.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, { source: 'cart-items' }));
    } catch (error) {
      this.#cancelLineExit();
      deferred.reject(error);
    } finally {
      this.#setLoading(false);
    }
  }

  async #settleLineExit() {
    const exit = this.#lineExit;
    if (!exit) return
    this.#lineExit = null;
    await exit.done;
  }

  #cancelLineExit() {
    const exit = this.#lineExit;
    if (!exit) return
    this.#lineExit = null;
    for (const el of exit.rows) {
      el.classList.remove('is-going', 'is-collapsing');
      el.style.removeProperty('block-size');
    }
  }

  #onCartUpdate = (event) => {
    if (event instanceof CartAddEvent) this._onCartAdd(event);
  }

  _onCartAdd() {
    this._refresh();
  }

  async _refresh() {
    try {
      const doc = await fetchSectionDoc(`${this.cartUrl}?section_id=${this.sectionId}`);
      const next = doc.querySelector('cart-items');
      if (!next) return
      const openPanels = [...this.querySelectorAll('details[accordion-item]')]
        .map((d, i) => (d.open ? i : -1))
        .filter((i) => i >= 0);
      const termsChecked = this._readTermsChecked();
      const wasEmpty = this.classList.contains('is-empty');
      this.innerHTML = next.innerHTML;
      this.querySelectorAll('details[accordion-item]').forEach((d, i) => {
        if (openPanels.includes(i)) d.setAttribute('open', '');
      });
      this._restoreTermsChecked(termsChecked);
      this._syncCheckoutButtons();
      this._populateProvinces();
      const nowEmpty = next.classList.contains('is-empty');
      this.classList.toggle('is-empty', nowEmpty);
      if (nowEmpty && !wasEmpty) this.#dealEmpty();
    } catch (err) {
      if (err?.status) return
      window.location.assign(this.cartUrl);
    }
  }

  #dealEmpty() {
    const panel = this.querySelector('[cart-empty]');
    if (!panel) return
    panel.classList.add('is-dealing');
    void panel.offsetHeight;
    panel.classList.add('is-shown');
  }

  async #applyDiscount() {
    if (this.#discountBusy) return
    const form = this.querySelector('[discount-form]');
    const input = form?.querySelector('input');
    const code = input?.value.trim();
    if (!code) {
      input?.focus();
      return
    }

    this.#discountBusy = true;
    const applyButton = form.querySelector('button');
    applyButton?.classList.add('is-loading');
    this.#setLoading(true);
    let discountDeferred = null;
    try {
      const current = await (await fetch(`${this.#root}cart.js`)).json();
      const codes = (current.discount_codes || []).map((d) => d.code);
      if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);

      discountDeferred = dispatchCartDiscountUpdate(form, { codes });

      const resp = await fetch(`${this.#root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      if (!resp.ok) throw new Error(`cart/update.js responded ${resp.status}`)
      const cart = await resp.json();
      const applied = cart.discount_codes?.some(
        (d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable
      );

      discountDeferred.resolve({
        cart: await toCartSummary(cart),
        userErrors: applied ? [] : [{ code: 'INVALID', message: `${code} is not applicable`, field: ['discountCodes'] }],
      });
      discountDeferred = null;

      if (!applied) {
        const kept = codes.filter((c) => c.toLowerCase() !== code.toLowerCase());
        await fetch(`${this.#root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: kept.join(',') }),
        }).catch(() => {});
        this.#setDiscountMessage('error');
        return
      }

      input.value = '';
      await this._refresh();
      const hasChip = [...this.querySelectorAll('[discount-remove]')].some(
        (chip) => chip.getAttribute('discount-remove').toLowerCase() === code.toLowerCase()
      );
      this.#setDiscountMessage(hasChip ? null : 'shipping');
      document.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, { source: 'cart-items' }));
    } catch (error) {
      discountDeferred?.reject(error);
    } finally {
      applyButton?.classList.remove('is-loading');
      this.#setLoading(false);
      this.#discountBusy = false;
    }
  }

  async #removeDiscount(code) {
    if (!code || this.#discountBusy) return
    this.#discountBusy = true;
    this.#setLoading(true);
    let discountDeferred = null;
    try {
      const current = await (await fetch(`${this.#root}cart.js`)).json();
      const kept = (current.discount_codes || [])
        .map((d) => d.code)
        .filter((c) => c.toLowerCase() !== code.toLowerCase());

      const form = this.querySelector('[discount-form]');
      discountDeferred = dispatchCartDiscountUpdate(form ?? this, { codes: kept });

      const resp = await fetch(`${this.#root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: kept.join(',') }),
      });
      if (!resp.ok) throw new Error(`cart/update.js responded ${resp.status}`)
      const cart = await resp.json();
      discountDeferred.resolve({ cart: await toCartSummary(cart) });
      await this._refresh();
      this.#setDiscountMessage(null);
      document.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, { source: 'cart-items' }));
    } catch (error) {
      discountDeferred?.reject(error);
    } finally {
      this.#setLoading(false);
      this.#discountBusy = false;
    }
  }

  #setDiscountMessage(type) {
    const form = this.querySelector('[discount-form]');
    if (!form) return
    form.querySelector('[discount-error]')?.toggleAttribute('hidden', type !== 'error');
    form.querySelector('[discount-shipping-note]')?.toggleAttribute('hidden', type !== 'shipping');
    form.querySelector('[discount-field]')?.classList.toggle('cart-discount-form--error', type === 'error');
  }

  async #estimateShipping(button) {
    const root = button.closest('[shipping-estimator]');
    const results = root?.querySelector('[shipping-results]');
    if (!results) return

    const country = root.querySelector('[shipping-country]')?.value || '';
    const province = root.querySelector('[shipping-province]')?.value || '';
    const zip = root.querySelector('[shipping-zip]')?.value || '';

    const params = new URLSearchParams();
    if (country) params.set('shipping_address[country]', country);
    if (province) params.set('shipping_address[province]', province);
    if (zip) params.set('shipping_address[zip]', zip);

    results.hidden = false;
    results.classList.remove('cart-shipping__results--error');
    results.textContent = '…';

    try {
      const resp = await fetch(`${this.cartUrl}/shipping_rates.json?${params.toString()}`);
      const data = await resp.json();
      const rates = data?.shipping_rates || [];

      if (!resp.ok) {
        this.#showShippingMessage(results, 'shipping-error-text');
        return
      }
      if (rates.length === 0) {
        this.#showShippingMessage(results, 'shipping-empty-text');
        return
      }

      const intro = document.createElement('p');
      intro.className = 'cart-shipping__results-intro';
      intro.textContent = root.querySelector('[shipping-intro-text]')?.textContent || '';

      const list = document.createElement('ul');
      for (const rate of rates) {
        const li = document.createElement('li');
        li.textContent = `${rate.name}: ${rate.price}`;
        list.appendChild(li);
      }
      results.classList.remove('cart-shipping__results--error');
      results.replaceChildren(intro, list);
    } catch {
      this.#showShippingMessage(results, 'shipping-error-text');
    }
  }

  #showShippingMessage(results, attr) {
    const root = results.closest('[shipping-estimator]') ?? this;
    const text = root.querySelector(`[${attr}]`)?.textContent || '';
    results.hidden = false;
    results.classList.add('cart-shipping__results--error');
    results.textContent = text;
  }

  _populateProvinces() {
    const country = this.querySelector('[shipping-country]');
    const province = this.querySelector('[shipping-province]');
    if (!country || !province) return

    let provinces = [];
    try {
      provinces = JSON.parse(country.options[country.selectedIndex]?.getAttribute('data-provinces') || '[]');
    } catch {
      provinces = [];
    }

    const field = province.closest('[shipping-province-field]');
    province.replaceChildren();

    if (provinces.length === 0) {
      province.disabled = true;
      if (field) field.hidden = true;
      return
    }

    for (const [name] of provinces) {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      province.appendChild(option);
    }
    province.disabled = false;
    if (field) field.hidden = false;
  }

  #setLoading(state) {
    this.toggleAttribute('aria-busy', state);
    this.classList.toggle('is-loading', state);
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

function fillPercent(el) {
  return el ? parseFloat(getComputedStyle(el).getPropertyValue('--progress')) || 0 : 0
}

class CartDrawer extends CartItems {
  #ownController = null

  connectedCallback() {
    super.connectedCallback();
    this.#ownController = new AbortController();
    const { signal } = this.#ownController;

    this.addEventListener('click', this.#onClearNote, { signal });
    this.addEventListener('input', this.#onNoteInput, { signal });


    this.closest('drawer-component')?.addEventListener(ThemeEvents.drawerOpen, this.#onDrawerOpen, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#ownController?.abort();
  }

  get #drawer() {
    return this.closest('drawer-component')
  }

  get _eventContext() {
    return 'dialog'
  }

  get _playsBarOnConnect() {
    return false
  }

  #onDrawerOpen = (event) => {
    if (event.target !== this.#drawer) return
    this._playShippingBar();
    dispatchCartView(this, 'dialog');
  }

  async _refresh(html = null) {
    try {
      let doc = null;
      if (html) {
        doc = parseHTML(html);
        if (!doc.querySelector('cart-drawer')) doc = null;
      }
      if (!doc) {
        doc = await fetchSectionDoc(`${rootUrl()}?section_id=${this.sectionId}`);
      }
      const next = doc.querySelector('cart-drawer');
      if (!next) return

      const wasEmpty = this.classList.contains('is-empty');
      const nowEmpty = next.classList.contains('is-empty');
      const termsChecked = this._readTermsChecked();
      const barFrom = fillPercent(this.querySelector('[progress-fill]'));

      if (wasEmpty !== nowEmpty) {
        this.innerHTML = next.innerHTML;
        this.classList.toggle('is-empty', nowEmpty);
        this._populateProvinces();
        if (nowEmpty) this.#dealEmpty();
      } else {
        for (const sel of [
          '[data-cart-region="progress"]',
          '[data-cart-region="lines"]',
          '[data-cart-region="summary"]',
          '[data-cart-region="discount-codes"]',
        ]) {
          const live = this.querySelector(sel);
          const fresh = next.querySelector(sel);
          if (live && fresh) live.innerHTML = fresh.innerHTML;
        }
      }

      this._restoreTermsChecked(termsChecked);
      this._syncCheckoutButtons();
      this.#travelShippingBar(barFrom);
    } catch (err) {
      if (err?.status) return
      window.location.assign(this.cartUrl);
    }
  }

  #travelShippingBar(from) {
    if (reducedMotion.matches) return
    const fill = this.querySelector('[progress-fill]');
    const to = fillPercent(fill);
    if (!fill || to <= 0 || Math.abs(to - from) < 0.5) return
    fill.style.transform = `scaleX(${from / to})`;
    void fill.offsetWidth;
    fill.style.transform = '';
  }

  #dealEmpty() {
    const panel = this.querySelector('.cart-drawer__empty');
    if (!panel) return
    panel.classList.add('is-dealing');
    void panel.offsetHeight;
    panel.classList.add('is-shown');
  }

  _onCartAdd(event) {
    const drawer = this.#drawer;
    if (!drawer) return
    const html = event.detail?.data?.sections?.[this.sectionId] ?? null;
    this._refresh(html).then(() => {
      if (!drawer.isOpen) drawer.open();
    });
  }

  #onSectionSelect = (event) => {
    if (event.target.contains(this)) this.#drawer?.open();
  }

  #onSectionDeselect = (event) => {
    if (event.target.contains(this)) this.#drawer?.close();
  }

  #onClearNote = (event) => {
    if (!event.target.closest('[notes-clear]')) return
    event.preventDefault();
    const note = this.querySelector('[name="note"]');
    if (!note) return
    note.value = '';
    note.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncClearState();
  }

  #onNoteInput = (event) => {
    if (event.target.matches('[name="note"]')) this.#syncClearState();
  }

  #syncClearState() {
    const note = this.querySelector('[name="note"]');
    const clear = this.querySelector('[notes-clear]');
    if (note && clear) clear.toggleAttribute('disabled', note.value.trim() === '');
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}


class CartRecommendations extends HTMLElement {
  #abortController = null

  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return
    this.#abortController = new AbortController();
    this.#load(url, this.#abortController.signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async #load(url, signal) {
    try {
      const doc = await fetchSectionDoc(url, { signal });
      const slider = doc.querySelector('slider-component');
      if (slider) this.replaceChildren(slider);
    } catch {
    } finally {
      if (!this.querySelector('[slide]')) {
        this.closest('.cart-drawer__upsell')?.setAttribute('hidden', '');
      }
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}

class CartBadge extends HTMLElement {
  static #pending = new WeakMap()
  static #announced = new WeakSet()

  #abort = null

  connectedCallback() {
    this.#abort = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, (e) => this.#onCartUpdate(e), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  async #onCartUpdate(event) {
    const cart = await CartBadge.#resolve(event);
    if (!cart) return
    this.#render(cart);

    if (!CartBadge.#announced.has(event)) {
      CartBadge.#announced.add(event);
      const live = document.querySelector('[cart-live-region]');
      if (live) live.textContent = themeString('cart.updated').replace('[count]', cart.item_count);
    }
  }

  #render({ item_count: count, total_price: total }) {
    this.querySelectorAll('[cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
    if (typeof total === 'number') {
      const money = formatMoney(total, this.getAttribute('money-format'));
      this.querySelectorAll('[cart-total]').forEach((el) => { el.textContent = money; });
    }
  }

  static #resolve(event) {
    const { data, resource } = event.detail ?? {};
    const count = data?.item_count ?? resource?.item_count;
    const total = data?.total_price ?? resource?.total_price;
    if (typeof count === 'number' && typeof total === 'number') {
      return Promise.resolve({ item_count: count, total_price: total })
    }
    if (!CartBadge.#pending.has(event)) {
      CartBadge.#pending.set(event, fetch(`${rootUrl()}cart.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null));
    }
    return CartBadge.#pending.get(event)
  }
}

if (!customElements.get('cart-badge')) {
  customElements.define('cart-badge', CartBadge);
}

const GRACE = 120;
const STEP_CAP$1 = 4;

class HeaderComponent extends HTMLElement {
  #abort = null
  #openItem = null
  #restoringFocus = false
  #closeTimer = null
  #lastScroll = 0
  #actionsMQ = null
  #portables = null
  #searchMQ = null
  #searchRelocation = null
  #hideTimers = new Map()
  #morphing = null
  #morphTimer = null
  #modals = []
  #initRaf = null
  #height = 0
  #resizeObserver = null

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#relocateNavItems();
    this.#relocateModals();
    this.#setupPortableActions(signal);

    this.#initRaf = requestAnimationFrame(() => this.#init(signal));
  }

  #init(signal) {
    if (!this.isConnected) return

    this.#relocateDrawerItems();
    this.#setupSearchRelocation(signal);
    this.#setupDrawerPlaceholder();

    this.items = Array.from(this.querySelectorAll('[header-menu-item]'));
    this.panels = new Map();
    this.querySelectorAll('[header-panel]').forEach((panel) => {
      this.panels.set(panel.getAttribute('menu-item'), panel);
    });

    this.items.forEach((item) => {
      const key = item.getAttribute('menu-item');
      if (!this.panels.has(key)) return

      item.addEventListener('mouseenter', () => this.#scheduleOpen(key), { signal });
      item.addEventListener('mouseleave', () => this.#scheduleClose(), { signal });
      item.addEventListener('focusin', () => {
        if (!this.#restoringFocus) this.#open(key);
      }, { signal });
      item.addEventListener('focusout', (e) => {
        const panel = this.panels.get(key);
        if (!item.contains(e.relatedTarget) && !panel?.contains(e.relatedTarget)) this.#scheduleClose();
      }, { signal });
    });

    this.panels.forEach((panel, key) => {
      const owner = this.items.find((i) => i.getAttribute('menu-item') === key);
      panel.addEventListener('mouseenter', () => this.#cancelClose(), { signal });
      panel.addEventListener('mouseleave', (e) => {
        if (owner?.contains(e.relatedTarget)) return
        this.#scheduleClose();
      }, { signal });
      panel.addEventListener('focusin', () => this.#cancelClose(), { signal });
      panel.addEventListener('focusout', (e) => {
        if (e.target?.disabled) return
        if (!panel.contains(e.relatedTarget)) this.#scheduleClose();
      }, { signal });
    });

    document.addEventListener(ThemeEvents.drawerOpen, (e) => {
      if (e.target.id === 'search-drawer') this.classList.add('is-search-open');
      if (e.target.id === 'header-menu-drawer') document.getElementById('search-drawer')?.close?.();
      if (!e.target.querySelector?.('[sl-map]')) return
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }, { signal });

    document.addEventListener(ThemeEvents.drawerClose, (e) => {
      if (e.target.id === 'search-drawer') this.classList.remove('is-search-open');
    }, { signal });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.#openItem) return
      const key = this.#openItem;
      const focusInPanel = this.panels.get(key)?.contains(document.activeElement);
      this.#close();
      if (focusInPanel) this.#focusTrigger(key);
    }, { signal });

    this.#setupFlyoutSubs(signal);

    if (this.hasAttribute('data-sticky') || this.hasAttribute('data-transparent')) {
      this.#lastScroll = window.scrollY;
      this.#height = this.offsetHeight;
      this.#resizeObserver = new ResizeObserver(() => {
        this.#height = this.offsetHeight;
      });
      this.#resizeObserver.observe(this);
      window.addEventListener('scroll', () => this.#onScroll(), { signal, passive: true });
      this.#onScroll();
    }

  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#initRaf);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#morphTimer);
    this.#hideTimers.forEach((t) => clearTimeout(t));
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#abort?.abort();
    const s = this.#searchRelocation;
    if (s && s.wrap.parentElement === s.mount) s.anchor.after(s.wrap);
    this.#modals.forEach((el) => el.remove());
    this.#modals = [];
  }

  #relocateNavItems() {
    const list = this.querySelector('[header-menu-list]');
    if (!list) return
    this.querySelectorAll('[header-nav-source]').forEach((item) => {
      item.removeAttribute('header-nav-source');
      list.appendChild(item);
    });

    const owners = new Map();
    this.querySelectorAll('[nav-claim]').forEach((el) => {
      const key = el.getAttribute('nav-claim');
      const by = el.getAttribute('nav-claim-by');
      if (!owners.has(key)) owners.set(key, by);
      else if (owners.get(key) !== by) el.remove();
    });

    this.querySelectorAll('template[header-nav-caret]').forEach((tpl) => {
      const key = tpl.getAttribute('header-nav-caret');
      const item = Array.from(list.children).find((i) => i.getAttribute('menu-item') === key);
      const link = item?.querySelector('[header-menu-link]');
      if (link) {
        item.querySelector('[header-flyout]')?.remove();
        link.setAttribute('header-menu-trigger', '');
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');
        if (!link.querySelector('[header-menu-caret]')) link.appendChild(tpl.content.cloneNode(true));
      }
      tpl.remove();
    });
  }

  #relocateDrawerItems() {
    if (!this.querySelector('template[header-drawer-source]')) return
    const drilldown = document.getElementById('header-menu-drawer')?.querySelector('menu-drilldown');
    if (!drilldown) {
      requestAnimationFrame(() => this.#relocateDrawerItems());
      return
    }
    const rootList = drilldown.querySelector('[drilldown-panel="root"] [header-drawer-list]');
    const separateMenu = this.hasAttribute('separate-mobile-menu');
    this.querySelectorAll('template[header-drawer-source]').forEach((tpl) => {
      const frag = tpl.content.cloneNode(true);
      const retarget = separateMenu ? null : tpl.getAttribute('data-retarget');
      const trigger = frag.querySelector('[header-drawer-trigger]');
      if (retarget) {
        const existing = rootList?.querySelector(`[data-menu-key="${retarget}"]`);
        if (existing?.hasAttribute('drilldown-open')) {
          const oldPanel = existing.getAttribute('drilldown-open');
          drilldown.querySelector(`[drilldown-panel="${oldPanel}"]`)?.remove();
          existing.setAttribute('drilldown-open', `drawer-${retarget}`);
        } else if (existing && trigger) {
          const button = trigger.querySelector('[drilldown-open]');
          const text = existing.querySelector('[header-drawer-link-text]');
          if (text) button.querySelector('[header-drawer-link-text]')?.replaceWith(text);
          existing.replaceWith(button);
        } else if (trigger && rootList) {
          rootList.appendChild(trigger);
        }
        frag.querySelectorAll('[header-drawer-panel]').forEach((p) => drilldown.appendChild(p));
      } else {
        if (trigger && rootList) rootList.appendChild(trigger);
        frag.querySelectorAll('[header-drawer-panel]').forEach((p) => drilldown.appendChild(p));
      }
      tpl.remove();
    });
    drilldown.registerPanels?.();
  }

  #relocateModals() {
    this.querySelectorAll('[header-modal-source]').forEach((el) => {
      el.removeAttribute('header-modal-source');
      document.body.appendChild(el);
      this.#modals.push(el);
    });
  }

  #setupFlyoutSubs(signal) {
    this.querySelectorAll('[header-flyout-item]').forEach((item) => {
      const sub = item.querySelector('[header-flyout-sub]');
      if (!sub) return
      const flyout = item.closest('[header-panel]');
      if (!flyout) return
      const position = () => {
        const itemRect = item.getBoundingClientRect();
        const flyoutRect = flyout.getBoundingClientRect();
        const subHeight = sub.getBoundingClientRect().height;
        let top = itemRect.top - flyoutRect.top;
        if (top + subHeight > flyoutRect.height) {
          top = Math.max(0, itemRect.bottom - flyoutRect.top - subHeight);
        }
        sub.style.setProperty('--flyout-sub-top', `${top}px`);
      };
      item.addEventListener('mouseenter', position, { signal });
      item.addEventListener('focusin', position, { signal });
    });
  }

  #setupPortableActions(signal) {
    const items = Array.from(this.querySelectorAll('[header-action-portable]'));
    if (!items.length) return
    this.#portables = items.map((el) => {
      const anchor = document.createComment('header-action');
      el.before(anchor);
      return { el, anchor }
    });
    this.#actionsMQ = window.matchMedia('(min-width: 1024px)');
    this.#actionsMQ.addEventListener('change', () => this.#placeActions(), { signal });
    this.#placeActions();
  }

  #placeActions() {
    const footer = document.querySelector('[header-drawer-actions]');
    if (!footer || !this.#portables) return
    const mobile = !this.#actionsMQ.matches;
    const storeMount = document.querySelector('[header-store-mount]');
    this.#portables.forEach(({ el, anchor }) => {
      el.classList.add('is-placed');
      if (mobile) {
        if (el.parentElement !== footer) footer.appendChild(el);
        return
      }
      const target = el.hasAttribute('header-store') && storeMount ? storeMount : null;
      if (target) {
        if (el.parentElement !== target) target.appendChild(el);
      } else if (el.parentElement === footer) {
        anchor.after(el);
      }
    });
  }

  #setupSearchRelocation(signal) {
    if (!this.querySelector('[header-layout="compact"]')) return
    const wrap = document.querySelector('[search-mobile-relocatable]');
    const mount = document.querySelector('[header-drawer-search-mount]');
    if (!wrap || !mount) return
    const anchor = document.createComment('search-wrap');
    wrap.before(anchor);
    this.#searchRelocation = { wrap, anchor, mount };
    this.#searchMQ = window.matchMedia('(min-width: 1024px)');
    this.#searchMQ.addEventListener('change', () => this.#placeSearch(), { signal });
    this.#placeSearch();
  }

  #setupDrawerPlaceholder() {
    const tpl = this.querySelector('template[header-drawer-placeholder]') || document.querySelector('template[header-drawer-placeholder]');
    const input = document.getElementById('search-drawer-input');
    if (!tpl || !input) return
    input.parentElement.querySelector('.search-drawer__ph')?.remove();
    input.after(tpl.content.cloneNode(true));
  }

  #placeSearch() {
    if (!this.#searchRelocation) return
    const { wrap, anchor, mount } = this.#searchRelocation;
    const mobile = !this.#searchMQ.matches;
    wrap.closest('drawer-component')?.close();
    if (mobile) {
      if (wrap.parentElement !== mount) mount.appendChild(wrap);
    } else if (wrap.parentElement === mount) {
      anchor.after(wrap);
    }
  }


  #scheduleOpen(key) {
    this.#cancelClose();
    this.#open(key);
  }

  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#close(), GRACE);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
  }

  #warm(panel) {
    for (const el of panel.querySelectorAll('*')) {
      if (el instanceof SliderComponent) el.boot();
    }
  }

  #stagger(panel) {
    const step = cssTimeMs(this, '--menu-step', 30);
    panel.querySelectorAll('[menu-reveal]').forEach((el) => {
      const n = parseInt(el.getAttribute('menu-reveal-step'), 10) || 0;
      el.style.setProperty('--reveal-delay', `${Math.min(n, STEP_CAP$1) * step}ms`);
    });
    panel.querySelectorAll('[slider-controls]').forEach((el) => {
      el.style.setProperty('--reveal-delay', `${step}ms`);
    });
  }

  #clearMorph() {
    clearTimeout(this.#morphTimer);
    if (!this.#morphing) return
    this.#morphing.classList.remove('is-morphing');
    this.#morphing.style.height = '';
    this.#morphing = null;
  }

  #morphCard(panel, fromH) {
    const card = panel.querySelector('[header-panel-card]');
    if (!card || !fromH) return

    this.#clearMorph();
    const toH = card.getBoundingClientRect().height;
    if (Math.abs(toH - fromH) < 1) return

    card.style.height = `${fromH}px`;
    void card.offsetWidth;
    card.classList.add('is-morphing');
    card.style.height = `${toH}px`;

    this.#morphing = card;
    this.#morphTimer = setTimeout(() => this.#clearMorph(), cssTimeMs(this, '--dur-menu-content', 260) + 60);
  }

  #open(key) {
    if (this.#openItem === key) return

    const item = this.items.find((i) => i.getAttribute('menu-item') === key);
    const panel = this.panels.get(key);
    if (!panel) return

    this.#cancelClose();
    clearTimeout(this.#hideTimers.get(key));

    const prevKey = this.#openItem;
    const prevPanel = prevKey !== null && prevKey !== key ? this.panels.get(prevKey) : null;
    const bleed = (p) => !p.hasAttribute('header-flyout');
    const swapping = !!prevPanel && bleed(prevPanel) && bleed(panel);
    let fromH = 0;
    if (swapping) {
      const prev = prevPanel.querySelector('[header-panel-card]');
      if (prev) fromH = prev.getBoundingClientRect().height;
      this.classList.add('is-swapping');
      this.#collapse(prevKey, true);
    } else if (prevPanel) {
      this.#collapse(prevKey);
    }

    panel.classList.remove('is-closing');
    panel.setAttribute('open', '');
    this.#warm(panel);
    this.#stagger(panel);
    void panel.offsetWidth;
    panel.classList.add('is-open');
    if (item && bleed(panel)) this.#bridge(panel, item);

    if (swapping) {
      this.#morphCard(panel, fromH);
      void panel.offsetWidth;
      this.classList.remove('is-swapping');
    }

    item?.classList.add('is-open');
    item?.querySelector('[header-menu-trigger]')?.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-menu-open', '');
    this.#openItem = key;

    panel.querySelector('menu-tabs')?.replay?.();
  }

  #bridge(panel, item) {
    const lift = new DOMMatrix(getComputedStyle(panel).transform).m42;
    const gap = panel.getBoundingClientRect().top - lift - item.getBoundingClientRect().bottom;
    panel.style.setProperty('--menu-bridge', `${Math.max(0, gap)}px`);
  }

  #collapse(key, instant = false) {
    const panel = this.panels.get(key);
    const item = this.items.find((i) => i.getAttribute('menu-item') === key);
    item?.classList.remove('is-open');
    item?.querySelector('[header-menu-trigger]')?.setAttribute('aria-expanded', 'false');
    if (!panel || !panel.hasAttribute('open')) return

    panel.classList.remove('is-open');
    panel.querySelectorAll('video-player').forEach((v) => v.pause?.());

    if (instant) {
      panel.removeAttribute('open');
      return
    }
    panel.classList.add('is-closing');
    this.#hideTimers.set(
      key,
      setTimeout(() => {
        if (this.#openItem === key) return
        panel.classList.remove('is-closing');
        panel.removeAttribute('open');
      }, cssTimeMs(this, '--dur-menu-out', 220) + 20)
    );
  }

  #focusTrigger(key) {
    const item = this.items.find((i) => i.getAttribute('menu-item') === key);
    this.#restoringFocus = true;
    item?.querySelector('[header-menu-trigger], a, button')?.focus();
    this.#restoringFocus = false;
  }

  #close() {
    clearTimeout(this.#closeTimer);
    if (!this.#openItem) return
    this.#clearMorph();
    const key = this.#openItem;
    this.#openItem = null;
    this.removeAttribute('data-menu-open');
    this.#collapse(key);
  }


  #onScroll() {
    const y = window.scrollY;
    if (this.hasAttribute('data-transparent')) {
      this.toggleAttribute('data-scrolled', y > 8);
    }
    if (this.hasAttribute('data-sticky') && this.getAttribute('data-sticky-style') !== 'always') {
      const goingDown = y > this.#lastScroll && y > this.#height;
      this.toggleAttribute('data-hidden', goingDown && !this.#openItem);
    }
    this.#lastScroll = y;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

const whenIdle = (fn) =>
  'requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1);

const afterLoad = (fn) =>
  document.readyState === 'complete' ? fn() : window.addEventListener('load', fn, { once: true });

const afterLoadIdle = (fn) => afterLoad(() => whenIdle(fn));

class DeferredAccount extends HTMLElement {
  #observer = null

  connectedCallback() {
    this.addEventListener('pointerenter', this.stamp, { once: true });
    this.addEventListener('focusin', this.stamp, { once: true });

    if (this.getAttribute('when') === 'drawer') {
      this.closest('drawer-component')?.addEventListener(ThemeEvents.drawerOpen, this.stamp, { once: true });
      return
    }
    afterLoadIdle(() => this.#stampWhenVisible());
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #stampWhenVisible() {
    const placeholder = this.querySelector('[account-placeholder]');
    if (!placeholder) return
    this.#observer = new ResizeObserver(() => {
      if (placeholder.checkVisibility()) this.stamp();
    });
    this.#observer.observe(placeholder);
  }

  stamp = () => {
    const template = this.querySelector('template');
    const placeholder = this.querySelector('[account-placeholder]');
    if (!template || !placeholder) return
    this.#observer?.disconnect();
    placeholder.replaceWith(template.content.cloneNode(true));
    template.remove();
  }
}

if (!customElements.get('deferred-account')) customElements.define('deferred-account', DeferredAccount);

const FOCUSABLE = 'a[href],button:not([disabled])';

class MenuDrilldown extends HTMLElement {
  #abort = null
  #panels = new Map()
  #stack = ['root']

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.registerPanels();

    this.addEventListener('click', (e) => {
      const opener = e.target.closest('[drilldown-open]');
      if (opener) {
        this.#drill(opener.getAttribute('drilldown-open'));
        return
      }
      if (e.target.closest('[drilldown-back]')) this.#back();
    }, { signal });

    this.closest('drawer-component')?.addEventListener(ThemeEvents.drawerClose, () => this.#reset(), { signal });

    this.#render(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  registerPanels() {
    this.querySelectorAll('[drilldown-panel]').forEach((panel) => {
      if (this.#panels.has(panel.getAttribute('drilldown-panel'))) return
      this.#panels.set(panel.getAttribute('drilldown-panel'), panel);
      panel.classList.remove('is-active', 'is-prev');
      panel.toggleAttribute('inert', true);
    });
  }

  #drill(name) {
    if (!this.#panels.has(name)) this.registerPanels();
    if (!this.#panels.has(name) || this.#stack.at(-1) === name) return
    this.#stack.push(name);
    this.#render();
  }

  #back() {
    if (this.#stack.length < 2) return
    this.#stack.pop();
    this.#render();
  }

  #reset() {
    if (this.#stack.length < 2) return
    this.classList.add('is-resetting');
    this.#stack = ['root'];
    this.#render(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.remove('is-resetting'));
    });
  }

  #render(focus = true) {
    const current = this.#stack.at(-1);

    this.#panels.forEach((panel, name) => {
      const isActive = name === current;
      const isAncestor = !isActive && this.#stack.includes(name);
      panel.classList.toggle('is-active', isActive);
      panel.classList.toggle('is-prev', isAncestor);
      panel.toggleAttribute('inert', !isActive);
    });

    if (focus) {
      const active = this.#panels.get(current);
      active?.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
    }
    this.scrollLeft = 0;
  }
}

if (!customElements.get('menu-drilldown')) {
  customElements.define('menu-drilldown', MenuDrilldown);
}

const STEP_CAP = 4;
const HOVER_DELAY = 120;

class MenuTabs extends TabGroup {
  #abort = null
  #observer = null
  #fromH = 0
  #morphTimer = null
  #hoverTimer = null

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

    requestAnimationFrame(() => {
 (this.tabs || []).forEach((tab) => {
        tab.addEventListener('mouseenter', () => {
          if (!fine.matches) return
          clearTimeout(this.#hoverTimer);
          this.#hoverTimer = setTimeout(() => tab.click(), HOVER_DELAY);
        }, { signal });
        tab.addEventListener('mouseleave', () => clearTimeout(this.#hoverTimer), { signal });
        tab.addEventListener('click', () => this.#measure(), { signal, capture: true });
        tab.addEventListener('click', (event) => {
          if (!event.isTrusted) event.preventDefault();
        }, { signal });
        tab.addEventListener('keydown', () => this.#measure(), { signal, capture: true });
      });
      this.#watchPanes();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#morphTimer);
    clearTimeout(this.#hoverTimer);
  }

  #measure() {
    this.#fromH = this.getBoundingClientRect().height;
  }

  #watchPanes() {
    if (!this.panels?.length) return
    this.#observer = new MutationObserver((records) => {
      const shown = records.map((r) => r.target).find((el) => !el.hidden);
      if (!shown) return
      this.#show(shown);
      this.#morph();
    });
    this.panels.forEach((pane) => {
      this.#observer.observe(pane, { attributes: true, attributeFilter: ['hidden'] });
    });
  }

  #show(pane) {
    const step = cssTimeMs(this, '--menu-step', 30);
    pane.classList.remove('is-shown');
    pane.querySelectorAll('[tab-reveal]').forEach((col, i) => {
      col.style.setProperty('--reveal-delay', `${Math.min(i, STEP_CAP) * step}ms`);
    });
    void pane.offsetWidth;
    pane.classList.add('is-shown');
  }

  #morph() {
    const fromH = this.#fromH;
    this.#fromH = 0;
    if (!fromH) return

    clearTimeout(this.#morphTimer);
    this.classList.remove('is-morphing');
    this.style.height = '';
    const toH = this.getBoundingClientRect().height;
    if (Math.abs(toH - fromH) < 1) return

    this.style.height = `${fromH}px`;
    void this.offsetWidth;
    this.classList.add('is-morphing');
    this.style.height = `${toH}px`;
    this.#morphTimer = setTimeout(() => {
      this.classList.remove('is-morphing');
      this.style.height = '';
    }, cssTimeMs(this, '--dur-menu-content', 260) + 60);
  }

  replay() {
    const active = (this.panels || []).find((pane) => !pane.hidden);
    if (active) this.#show(active);
  }
}

if (!customElements.get('menu-tabs')) {
  customElements.define('menu-tabs', MenuTabs);
}

class AnnouncementBar extends SliderComponent {
  connectedCallback() {
    super.connectedCallback();

    this._onHold = () => this.swiper?.autoplay?.stop();
    this._onRelease = (event) => {
      if (event.type === 'focusout' && this.contains(event.relatedTarget)) return
      if (event.type === 'pointerleave' && this.contains(document.activeElement)) return
      if (this._rotates) this.swiper?.autoplay?.start();
    };
    this.addEventListener('pointerenter', this._onHold);
    this.addEventListener('focusin', this._onHold);
    this.addEventListener('pointerleave', this._onRelease);
    this.addEventListener('focusout', this._onRelease);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this._onHold);
    this.removeEventListener('focusin', this._onHold);
    this.removeEventListener('pointerleave', this._onRelease);
    this.removeEventListener('focusout', this._onRelease);
  }

  get _modules() {
    return [...super._modules, EffectFade]
  }

  get _rotates() {
    return !!this._config.autoplay && !reducedMotion.matches
  }

  get _swiperConfig() {
    return {
      ...super._swiperConfig,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 0,
      autoplay: this._rotates ? { ...this._config.autoplay, waitForTransition: false } : false,
    }
  }

  _setup() {
    super._setup();
    if (!this.swiper) return
    const syncLive = () => {
      const active = this.swiper.slides[this.swiper.activeIndex];
      this._queryAll('[announcement-message]').forEach((msg) =>
        msg.classList.toggle('is-live', !!active && active.contains(msg))
      );
    };
    this.swiper.on('slideChange', syncLive);
    syncLive();
  }
}

customElements.get('announcement-bar') || customElements.define('announcement-bar', AnnouncementBar);



let menuTiming = null;
const readMenuTiming = () => {
  if (menuTiming) return menuTiming
  const root = document.documentElement;
  menuTiming = {
    content: cssTimeMs(root, '--dur-menu-content', 260),
    out: cssTimeMs(root, '--dur-menu-out', 220),
  };
  return menuTiming
};

function predictiveSearchSupported() {
  try {
    return JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch === true
  } catch {
    return false
  }
}

class PredictiveSearch extends HTMLElement {
  #controller = null
  #debounceTimer = null

  connectedCallback() {
    const root = this.hasAttribute('scoped-inputs') ? this : document;

    this.panelInput = this.querySelector('[predictive-input]');
    this.driverInputs = [...root.querySelectorAll('[search-drawer-driver]')];
    this.inputs = [this.panelInput, ...this.driverInputs].filter(Boolean);

    this.idle = this.querySelector('[search-idle]');
    this.results = this.querySelector('[search-results]');
    this.clearBtns = [...root.querySelectorAll('[clear-search]')];
    this.noResultsTags = this.querySelector('[search-noresults-tags]');
    this.collapseBtn = this.querySelector('[search-collapse]');

    this.sectionId = this.getAttribute('section-id') || 'predictive-search';
    this.minChars = parseInt(this.getAttribute('min-chars') || '2', 10);
    this.predictiveEnabled =
      this.hasAttribute('predictive') && predictiveSearchSupported();

    if (this.inputs.length === 0) return

    this.inputs.forEach((el) => {
      el.addEventListener('input', this.#onInput);
      el.addEventListener('keydown', this.#onKeydown);
    });
    this.clearBtns.forEach((el) => el.addEventListener('click', this.#onClear));
    this.collapseBtn?.addEventListener('click', this.#onCollapse);

    this.scopeSelects = [...root.querySelectorAll('[search-scope]')].filter(
      (el) => root === this || !el.closest('predictive-search[scoped-inputs]')
    );
    this.scopeSelects.forEach((el) => el.addEventListener('change', this.#onScopeChange));

    this.forms = [...new Set(this.inputs.map((el) => el.form).filter(Boolean))];
    this.forms.forEach((el) => el.addEventListener('submit', this.#onSubmit));

    this.drawer = this.closest('drawer-component');
    this.#updateCollapseLabel();
    this.drawer?.addEventListener(ThemeEvents.drawerOpen, this.#onOpen);
    this.drawer?.addEventListener(ThemeEvents.drawerClose, this.#onClose);

    window.addEventListener('scroll', this.#onReposition, { passive: true });
    window.addEventListener('resize', this.#onReposition, { passive: true });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#resetTimer) this.#reset();
    clearTimeout(this.#debounceTimer);
    clearTimeout(this.#dealTimer);
    this.inputs?.forEach((el) => {
      el.removeEventListener('input', this.#onInput);
      el.removeEventListener('keydown', this.#onKeydown);
    });
    this.clearBtns?.forEach((el) => el.removeEventListener('click', this.#onClear));
    this.collapseBtn?.removeEventListener('click', this.#onCollapse);
    this.scopeSelects?.forEach((el) => el.removeEventListener('change', this.#onScopeChange));
    this.forms?.forEach((el) => el.removeEventListener('submit', this.#onSubmit));
    this.drawer?.removeEventListener(ThemeEvents.drawerOpen, this.#onOpen);
    this.drawer?.removeEventListener(ThemeEvents.drawerClose, this.#onClose);
    window.removeEventListener('scroll', this.#onReposition);
    window.removeEventListener('resize', this.#onReposition);
  }

  #visibleInput() {
    return this.inputs.find((el) => el.offsetParent !== null) ?? this.inputs[0]
  }

  #positionFlyout() {
    const header = document.querySelector('header.header');
    if (!header) return
    const nav = header.querySelector('.header__grid--desktop [header-nav-row]');
    const edge = nav?.offsetParent
      ? nav.getBoundingClientRect().top + nav.clientTop
      : header.getBoundingClientRect().bottom;
    const bottom = Math.max(0, Math.round(edge));
    const rect = header.getBoundingClientRect();
    const root = document.documentElement.style;
    root.setProperty('--search-flyout-top', `${bottom}px`);
    root.setProperty('--search-takeover-top', `${Math.max(0, Math.round(rect.top))}px`);
    root.setProperty('--search-takeover-bar', `${Math.round(rect.bottom - Math.max(0, rect.top))}px`);
  }

  #repositionTicking = false

  #onReposition = () => {
    if (!this.drawer?.hasAttribute('open') || this.#repositionTicking) return
    this.#repositionTicking = true;
    requestAnimationFrame(() => {
      this.#repositionTicking = false;
      if (this.drawer?.hasAttribute('open')) this.#positionFlyout();
    });
  }

  get #card() {
    if (this.drawer?.id !== 'search-drawer' || !this.drawer.hasAttribute('open')) return null
    if (!window.matchMedia('(min-width: 768px)').matches) return null
    return this.querySelector('[search-card]')
  }

  #onOpen = () => {
    if (this.#resetTimer) this.#reset();
    this.#setExpanded(this.#flyoutOpen);

    requestAnimationFrame(() => {
      this.#positionFlyout();
      if (this.drawer?.id === 'search-drawer') this.#visibleInput()?.focus();
      this.#dealIdle();
    });
  }

  #dealTimer = null

  #dealIdle() {
    if (!this.querySelector('[reveal]')) return
    replayReveal(this);
    clearTimeout(this.#dealTimer);
    this.#dealTimer = setTimeout(() => {
      if (!this.querySelector('[reveal].is-revealed')) replayReveal(this);
    }, 320);
  }

  #resetTimer = null

  #onClose = () => {
    this.#setExpanded(false);
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => this.#reset(), readMenuTiming().out + 40);
  }

  #reset() {
    clearTimeout(this.#resetTimer);
    this.#resetTimer = null;
    this.inputs.forEach((el) => (el.value = ''));
    this.classList.remove('has-value');
    this.#showIdle();
  }

  #onInput = (e) => {
    const raw = e.target.value;
    const value = raw.trim();
    this.inputs.forEach((el) => {
      if (el !== e.target) el.value = raw;
    });
    this.classList.toggle('has-value', value.length > 0);

    if (value.length > 0 && this.drawer?.id === 'search-drawer' && !this.drawer.isOpen) {
      this.drawer.open();
    }

    if (!this.predictiveEnabled) return

    clearTimeout(this.#debounceTimer);
    if (value.length < this.minChars) {
      this.#showIdle();
      return
    }
    this.#debounceTimer = setTimeout(() => this.#search(value), 200);
  }

  #onClear = () => {
    this.inputs.forEach((el) => (el.value = ''));
    this.classList.remove('has-value');
    this.#showIdle();
    this.#visibleInput()?.focus();
  }

  #updateCollapseLabel() {
    if (!this.collapseBtn) return
    const label = this.drawer?.id === 'search-drawer'
      ? this.collapseBtn.getAttribute('close-label')
      : this.collapseBtn.getAttribute('back-label');
    if (label) this.collapseBtn.setAttribute('aria-label', label);
  }

  #onCollapse = () => {
    if (this.drawer?.id === 'search-drawer') {
      this.drawer.close();
      return
    }
    this.inputs.forEach((el) => (el.value = ''));
    this.classList.remove('has-value');
    this.#showIdle();
    const fallback = this.drawer?.querySelector('[drawer-close]');
    if (this.contains(document.activeElement)) (fallback ?? this.drawer)?.focus();
  }

  #productType() {
    return this.scopeSelects?.find((el) => el.value)?.value ?? ''
  }

  #scopedQuery(value) {
    const type = this.#productType();
    return type ? `product_type:"${type}" AND ${value}` : value
  }

  #onScopeChange = (e) => {
    this.scopeSelects.forEach((el) => {
      if (el !== e.target) el.value = e.target.value;
    });
    if (!this.predictiveEnabled) return
    const value = (this.#visibleInput()?.value || '').trim();
    if (value.length < this.minChars) {
      this.#showIdle();
      return
    }
    this.#search(value);
  }

  #onSubmit = (e) => {
    const type = this.#productType();
    if (!type) return
    const form = e.currentTarget;
    const value = (form.querySelector('input[name="q"]')?.value || '').trim();
    if (!value || value.startsWith('product_type:')) return
    e.preventDefault();
    const params = new URLSearchParams({
      q: `product_type:"${type}" AND ${value}`,
      'options[prefix]': 'last',
    });
    window.location.href = `${form.getAttribute('action')}?${params}`;
  }

  get #flyoutOpen() {
    return this.drawer?.id === 'search-drawer' && this.drawer.isOpen
  }

  #setExpanded(state) {
    this.inputs.forEach((el) => el.setAttribute('aria-expanded', String(state)));
  }

  #showIdle() {
    this.#controller?.abort();
    morphHeight(this.#card, () => {
      if (this.results) {
        this.results.hidden = true;
        this.results.classList.remove('is-open');
        this.results.innerHTML = '';
      }
      if (this.idle) this.idle.hidden = false;
    }, readMenuTiming().content);
    this.options = [];
    this.selectedIndex = -1;
    this.inputs.forEach((el) => el.removeAttribute('aria-activedescendant'));
    this.#setExpanded(this.#flyoutOpen);
  }

  #stageResults(refresh) {
    if (!refresh) {
      requestAnimationFrame(() => this.results?.classList.add('is-open'));
      return
    }
    this.results.classList.add('is-open');
    this.results.querySelectorAll('[reveal]').forEach((el) => el.classList.add('is-revealed'));
    initReveal(this.results);
  }

  async #search(query) {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const root = rootUrl();
    const params = new URLSearchParams({ q: this.#scopedQuery(query), section_id: this.sectionId });
    params.append('resources[type]', 'query,product,collection,page,article');
    params.append('resources[limit]', '6');
    params.append('resources[limit_scope]', 'each');

    const searchDeferred = dispatchSearchUpdate(this, { query });

    try {
      const doc = await fetchSectionDoc(`${root}search/suggest?${params.toString()}`, {
        signal: this.#controller.signal,
      });
      const markup = doc.querySelector(`#shopify-section-${this.sectionId}`)?.innerHTML ?? '';
      const refresh = !this.results.hidden;

      morphHeight(this.#card, () => {
        this.results.innerHTML = markup;

        if (this.results.querySelector('[predictive-no-results]') && this.noResultsTags) {
          this.results.appendChild(this.noResultsTags.content.cloneNode(true));
        }

        if (this.idle) this.idle.hidden = true;
        this.results.hidden = false;
      }, readMenuTiming().content);

      searchDeferred.resolve({ totalCount: Number(doc.querySelector('[results-total]')?.getAttribute('results-total')) || 0 });
      this.#stageResults(refresh);
      this.#setExpanded(true);
      this.#indexOptions();
    } catch (err) {
      searchDeferred.reject(err);
      if (err?.name === 'AbortError') return
      this.#showIdle();
    }
  }

  #indexOptions() {
    this.results.setAttribute('role', 'listbox');
    this.options = [...this.results.querySelectorAll('a')];
    this.options.forEach((el, i) => {
      el.setAttribute('role', 'option');
      el.id = el.id || `predictive-option-${i}`;
      el.setAttribute('aria-selected', 'false');
    });
    this.selectedIndex = -1;
    this.inputs.forEach((el) => el.removeAttribute('aria-activedescendant'));
  }

  #onKeydown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        if (!this.options?.length) return
        e.preventDefault();
        this.#select(this.selectedIndex < 0 ? 0 : this.selectedIndex + 1);
        break
      case 'ArrowUp':
        if (!this.options?.length) return
        e.preventDefault();
        this.#select(this.selectedIndex <= 0 ? this.options.length - 1 : this.selectedIndex - 1);
        break
      case 'Enter':
        if (this.selectedIndex >= 0 && this.options?.[this.selectedIndex]) {
          e.preventDefault();
          this.options[this.selectedIndex].click();
        }
        break
      case 'Escape':
        if (this.classList.contains('has-value')) {
          e.stopPropagation();
          this.#onClear();
        }
        break
    }
  }

  #select(index) {
    const max = this.options.length - 1;
    this.selectedIndex = Math.max(0, Math.min(index, max));
    this.options.forEach((el, i) => {
      const on = i === this.selectedIndex;
      el.setAttribute('aria-selected', String(on));
      el.classList.toggle('is-highlighted', on);
    });
    const active = this.options[this.selectedIndex];
    this.inputs.forEach((el) => el.setAttribute('aria-activedescendant', active.id));
    active.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    this.#visibleInput()?.focus();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

const ROTATE_INTERVAL = 2500;

class RotatingPlaceholder extends HTMLElement {
  #timer = null

  connectedCallback() {
    const count = this.children.length - 1;
    if (count < 2) return

    const input = this.closest('form')?.querySelector('input[name="q"]');
    let i = count;

    this.#timer = setInterval(() => {
      if (input?.value) return

      if (reducedMotion.matches) {
        i = i <= 1 ? count : i - 1;
        this.style.setProperty('--ph-index', i);
        return
      }

      i -= 1;
      this.style.setProperty('--ph-index', i);

      if (i === 0) {
        let done = false;
        const reset = () => {
          if (done) return
          done = true;
          clearTimeout(fallback);
          this.removeEventListener('transitionend', reset);
          this.style.transition = 'none';
          i = count;
          this.style.setProperty('--ph-index', i);
          void this.offsetHeight;
          this.style.transition = '';
        };
        const fallback = setTimeout(reset, 600);
        this.addEventListener('transitionend', reset);
      }
    }, ROTATE_INTERVAL);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }
}

if (!customElements.get('rotating-placeholder')) {
  customElements.define('rotating-placeholder', RotatingPlaceholder);
}


class DropdownComponent extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[dropdown-trigger]');
    this._valueEl = this.querySelector('[dropdown-value]');
    this.panel = this.querySelector('[dropdown-panel]');
    this.input = this.querySelector('[dropdown-input]');
    this.options = Array.from(this.querySelectorAll('[dropdown-option]'));
    if (!this.trigger || !this.panel) return

    this._onTriggerClick = () => this.toggle();
    this._onPanelClick = event => this._handleSelect(event);
    this._onKeydown = event => this._handleKeydown(event);
    this._onDocumentClick = event => this._handleOutsideClick(event);
    this._onOtherOpen = event => {
      if (event.detail !== this) this.close();
    };

    this.trigger.addEventListener('click', this._onTriggerClick);
    this.panel.addEventListener('click', this._onPanelClick);
    this.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener(ThemeEvents.dropdownOpen, this._onOtherOpen);
  }

  disconnectedCallback() {
    clearTimeout(this._hideTimer);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener(ThemeEvents.dropdownOpen, this._onOtherOpen);
  }

  get open() {
    return this.classList.contains('is-open')
  }

  get value() {
    return this.panel?.querySelector('[aria-selected="true"]')?.getAttribute('value') ?? null
  }

  refreshOptions() {
    this.options = Array.from(this.querySelectorAll('[dropdown-option]'));
  }

  selectValue(value) {
    const option = this.options.find((opt) => opt.getAttribute('value') === String(value));
    if (option) this._select(option, { silent: true });
  }

  toggle() {
    this.open ? this.close() : this.show();
  }

  show() {
    document.dispatchEvent(new DropdownOpenEvent(this));
    clearTimeout(this._hideTimer);
    this.panel.hidden = false;
    void this.panel.offsetWidth;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');

    const current =
      this.panel.querySelector('[aria-selected="true"]') ||
      this.panel.querySelector('[aria-current="true"]') ||
      this.options[0];
    current?.focus();
  }

  close({ focusTrigger = false } = {}) {
    if (!this.open) return
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');

    const leave = this._panelLeaveMs();
    if (leave > 0) {
      this._hideTimer = setTimeout(() => {
        this.panel.hidden = true;
      }, leave);
    } else {
      this.panel.hidden = true;
    }

    if (focusTrigger) this.trigger.focus();
  }

  _panelLeaveMs() {
    const style = getComputedStyle(this.panel);
    const toMs = (value) => {
      const v = value.trim();
      return (parseFloat(v) || 0) * (v.endsWith('ms') ? 1 : 1000)
    };
    const delays = style.transitionDelay.split(',');
    return Math.max(
      0,
      ...style.transitionDuration.split(',').map((d, i) => toMs(d) + toMs(delays[i % delays.length] ?? '0s'))
    )
  }

  _handleSelect(event) {
    const option = event.target.closest('[dropdown-option]');
    if (!option) return
    this._select(option);
  }

  _select(option, { silent = false } = {}) {
    this.options.forEach(opt => opt.setAttribute('aria-selected', String(opt === option)));

    if (this._valueEl) {
      const label = option.querySelector('[dropdown-option-label]') || option;
      this._valueEl.textContent = label.textContent.trim();
    }

    if (silent) return
    this.dispatchEvent(new Event('change', { bubbles: true }));

    if (this.hasAttribute('dropdown-navigate')) {
      const url = option.getAttribute('value');
      if (url) {
        window.location.href = url;
        return
      }
    }

    if (this.input) {
      this.input.value = option.getAttribute('value');
      this.input.closest('form')?.submit();
      return
    }

    this.close({ focusTrigger: true });
  }

  _handleOutsideClick(event) {
    if (this.open && !this.contains(event.target)) this.close();
  }

  _handleKeydown(event) {
    if (!this.open) {
      if ((event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') && event.target === this.trigger) {
        event.preventDefault();
        this.show();
      }
      return
    }

    const currentIndex = this.options.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close({ focusTrigger: true });
        break
      case 'ArrowDown':
        event.preventDefault();
        this.options[Math.min(currentIndex + 1, this.options.length - 1)]?.focus();
        break
      case 'ArrowUp':
        event.preventDefault();
        this.options[Math.max(currentIndex - 1, 0)]?.focus();
        break
      case 'Home':
        event.preventDefault();
        this.options[0]?.focus();
        break
      case 'End':
        event.preventDefault();
        this.options[this.options.length - 1]?.focus();
        break
      case 'Tab':
        this.close();
        break
    }
  }
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}

class LocalizationSelector extends DropdownComponent {}

if (!customElements.get('localization-selector')) {
  customElements.define('localization-selector', LocalizationSelector);
}


class FooterComponent extends HTMLElement {
  connectedCallback() {
    this._stageRows();
  }

  _stageRows() {
    const blocks = [...this.querySelectorAll('[footer-blocks] > [reveal]')];
    if (!blocks.length) return

    const staged = blocks.some((b) => b.style.getPropertyValue('--reveal-delay'));

    let row = 0;
    let col = 0;
    let top = null;
    blocks.forEach((b) => {
      if (top === null || Math.abs(b.offsetTop - top) > 1) {
        top = b.offsetTop;
        row += 1;
        col = 0;
        b.setAttribute('footer-row', row);
        b.removeAttribute('reveal-with');
      } else {
        col += 1;
        b.removeAttribute('footer-row');
        b.setAttribute('reveal-with', `[footer-row="${row}"]`);
      }
      b.setAttribute('reveal-step', col);
    });

    if (staged && !this.querySelector('[reveal].is-revealed')) replayReveal(this);
  }
}

if (!customElements.get('footer-component')) {
  customElements.define('footer-component', FooterComponent, { extends: 'footer' });
}


const ROOT_MARGIN = '400px 0px';

function lazyElements(entries) {
  const designMode = Boolean(window.Shopify?.designMode);
  const pending = new Set(
    entries.map(([selector, load, mode]) => ({ selector, load, near: mode === 'near' && !designMode }))
  );
  let combined = '';
  const refresh = () => {
    combined = [...pending].map((e) => e.selector).join(',');
  };
  refresh();

  const load = (entry) => {
    if (!pending.delete(entry)) return
    refresh();
    entry.load().catch((error) => console.error(`[lazy-elements] ${entry.selector}`, error));
  };

  const watching = new Map();
  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (records) => {
            records.forEach((record) => {
              if (record.isIntersecting) release(record.target);
            });
          },
          { rootMargin: ROOT_MARGIN }
        )
      : null;

  const release = (target) => {
    const waiting = watching.get(target);
    watching.delete(target);
    observer.unobserve(target);
    waiting?.forEach(load);
  };

  let settle = 0;
  const sweep = () => {
    if (!watching.size) return
    const limit = window.innerHeight + 400
    ;[...watching.keys()].filter((t) => t.getBoundingClientRect().top < limit).forEach(release);
  };
  if (observer) {
    if ('onscrollend' in window) window.addEventListener('scrollend', sweep, { passive: true });
    else window.addEventListener('scroll', () => { clearTimeout(settle); settle = setTimeout(sweep, 150); }, { passive: true });
  }

  const watch = (entry, element) => {
    if (!observer) return load(entry)
    const target = element.closest('.shopify-section') || element;
    if (!watching.has(target)) {
      watching.set(target, new Set());
      observer.observe(target);
    }
    watching.get(target).add(entry);
  };

  const scan = (root) => {
    if (!combined || !(root.matches?.(combined) || root.querySelector(combined))) return
    for (const entry of [...pending]) {
      const matches = [...root.querySelectorAll(entry.selector)];
      if (root.matches?.(entry.selector)) matches.unshift(root);
      if (!matches.length) continue
      if (entry.near) matches.forEach((element) => watch(entry, element));
      else load(entry);
    }
  };

  scan(document);

  new MutationObserver((records) => {
    if (!pending.size) return
    records.forEach((record) =>
      record.addedNodes.forEach((node) => node.nodeType === Node.ELEMENT_NODE && scan(node))
    );
  }).observe(document.documentElement, { childList: true, subtree: true });
}



function cartUrl() {
  return `${rootUrl()}cart`
}

function cartDrawerHost() {
  const host = document.querySelector('drawer-component#cart-drawer');
  return host?.querySelector('cart-drawer') ? host : null
}

function cartElement() {
  return (
    cartDrawerHost()?.querySelector('cart-drawer') ??
    document.querySelector('cart-items') ??
    document.querySelector('cart-drawer')
  )
}

async function refreshCartSurfaces() {
  const surfaces = [...document.querySelectorAll('cart-items, cart-drawer')];
  await Promise.all(surfaces.map((surface) => surface._refresh?.()));
  document.dispatchEvent(new CartUpdateEvent(null, null, { source: 'standard-action' }));
}

function configureStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return

  actions.openCart?.configure({
    async handler() {
      const host = cartDrawerHost();
      if (!host) {
        window.location.assign(cartUrl());
        return
      }
      await host.querySelector('cart-drawer')?._refresh?.();
      host.open();
    },
  });

  actions.updateCart?.configure({
    eventTarget: (meta) => {
      if (meta.type === 'shopify:cart:lines-update' && meta.action === 'add') {
        return document.querySelector('product-form') ?? cartElement()
      }
      return cartElement()
    },
    async handler(defaultHandler) {
      const result = await defaultHandler();
      await refreshCartSurfaces();
      return result
    },
  });
}

lazyElements([
  ['product-form, variant-picker, product-media, product-lightbox, sticky-atc, purchase-options, complementary-products', () => __vitePreload(() => import('theme/product-form'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['facets-form-component, price-facet-component', () => __vitePreload(() => import('theme/facets'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['store-locator, sl-gallery-slider, store-locator-card-slider', () => __vitePreload(() => import('theme/store-locator'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['gift-card-recipient-form', () => __vitePreload(() => import('theme/gift-card-recipient-form'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['countdown-timer', () => __vitePreload(() => import('theme/countdown-timer'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['password-modal', () => __vitePreload(() => import('theme/password-modal'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['predictive-tabs', () => __vitePreload(() => import('theme/predictive-tabs'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['faq-accordions', () => __vitePreload(() => import('theme/faq-accordions'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['article-body', () => __vitePreload(() => import('theme/article-body'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['page-content', () => __vitePreload(() => import('theme/page-content'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['popup-component', () => __vitePreload(() => import('theme/popup'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['dock-component', () => __vitePreload(() => import('theme/dock'),true              ?[]:void 0,import.meta.url), 'presence'],
  ['cookie-banner', () => __vitePreload(() => import('theme/cookie-banner'),true              ?[]:void 0,import.meta.url), 'presence'],

  ['bold-collections', () => __vitePreload(() => import('theme/bold-collections'),true              ?[]:void 0,import.meta.url), 'near'],
  ['heading-with-image', () => __vitePreload(() => import('theme/heading-with-image'),true              ?[]:void 0,import.meta.url), 'near'],
  ['before-after-slider', () => __vitePreload(() => import('theme/before-after'),true              ?[]:void 0,import.meta.url), 'near'],
  ['marquee-track, scrollable-gallery, testimonial-gallery', () => __vitePreload(() => import('theme/marquee'),true              ?[]:void 0,import.meta.url), 'near'],
  ['blockquote-section', () => __vitePreload(() => import('theme/blockquote'),true              ?[]:void 0,import.meta.url), 'near'],
  ['video-carousel', () => __vitePreload(() => import('theme/video-carousel'),true              ?[]:void 0,import.meta.url), 'near'],
  ['media-with-text-stack', () => __vitePreload(() => import('theme/media-with-text-stack'),true              ?[]:void 0,import.meta.url), 'near'],
  ['showcase-gallery', () => __vitePreload(() => import('theme/showcase-gallery'),true              ?[]:void 0,import.meta.url), 'near'],
  ['timeline-story', () => __vitePreload(() => import('theme/timeline-story'),true              ?[]:void 0,import.meta.url), 'near'],
  ['info-table', () => __vitePreload(() => import('theme/info-table'),true              ?[]:void 0,import.meta.url), 'near'],
  ['collection-card-carousel', () => __vitePreload(() => import('theme/collection-card-carousel'),true              ?[]:void 0,import.meta.url), 'near'],
  ['shoppable-image', () => __vitePreload(() => import('theme/shoppable-image'),true              ?[]:void 0,import.meta.url), 'near'],
  ['image-with-products-carousel', () => __vitePreload(() => import('theme/image-with-products'),true              ?[]:void 0,import.meta.url), 'near'],
  ['product-bundle, bundle-product-card', () => Promise.all([__vitePreload(() => import('theme/product-bundle'),true              ?[]:void 0,import.meta.url), __vitePreload(() => import('theme/bundle-product-card'),true              ?[]:void 0,import.meta.url)]), 'near'],
  ['collection-statement', () => __vitePreload(() => import('theme/collection-statement'),true              ?[]:void 0,import.meta.url), 'near'],
  ['read-more-toggle', () => __vitePreload(() => import('theme/read-more'),true              ?[]:void 0,import.meta.url), 'near'],
  ['blog-list', () => __vitePreload(() => import('theme/blog-list'),true              ?[]:void 0,import.meta.url), 'near'],
  ['blog-filter', () => __vitePreload(() => import('theme/blog-filter'),true              ?[]:void 0,import.meta.url), 'near'],
  ['table-of-contents', () => __vitePreload(() => import('theme/table-of-contents'),true              ?[]:void 0,import.meta.url), 'near'],
  ['copy-link', () => __vitePreload(() => import('theme/copy-link'),true              ?[]:void 0,import.meta.url), 'near'],
  ['mini-cards-carousel', () => __vitePreload(() => import('theme/mini-cards'),true              ?[]:void 0,import.meta.url), 'near'],
  ['newsletter-image-strip', () => __vitePreload(() => import('theme/newsletter-image-strip'),true              ?[]:void 0,import.meta.url), 'near'],
  ['search-tabs', () => __vitePreload(() => import('theme/search-tabs'),true              ?[]:void 0,import.meta.url), 'near'],
  ['load-more-results', () => __vitePreload(() => import('theme/load-more-results'),true              ?[]:void 0,import.meta.url), 'near'],
  ['product-quiz, quiz-group-slider, quiz-result-modal', () => __vitePreload(() => import('theme/product-quiz'),true              ?[]:void 0,import.meta.url), 'near'],
  ['ugc-spotlight-slider, ugc-carousel, ugc-lightbox', () => __vitePreload(() => import('theme/ugc'),true              ?[]:void 0,import.meta.url), 'near'],
  ['subscription-plans', () => __vitePreload(() => import('theme/subscription-plans'),true              ?[]:void 0,import.meta.url), 'near'],
  ['discovery-results-slider, discovery-collage, discovery-section', () => __vitePreload(() => import('theme/discovery'),true              ?[]:void 0,import.meta.url), 'near'],
  ['related-products', () => __vitePreload(() => import('theme/related-products'),true              ?[]:void 0,import.meta.url), 'near'],
]);

document.addEventListener('DOMContentLoaded', () => {
  configureStandardActions();
  afterLoadIdle(() => {
    upgradeViewEvents();
    dispatchPageView();
  });
});

bootReveal();

export { morphHeight as A, rafThrottle as B, CartAddEvent as C, DrawerComponent as D, setButtonLabel as E, FilterUpdateEvent as F, watchTravellingFill as G, placeTravellingFill as H, BundleRemovedEvent as I, readTiming as J, BundleAddedEvent as K, tablistKeydownIndex as L, NestedCarousel as N, SliderComponent as S, ThemeEvents as T, dispatchCartLinesUpdate as a, fetchCartSummary as b, refreshCartAfterFailedAdd as c, dispatchProductSelect as d, dispatchCartError as e, fetchSectionDoc as f, setLabelText as g, storeScrollbarWidth as h, dispatchSearchUpdate as i, dispatchCollectionUpdate as j, replaceInner as k, replaceOuter as l, replaceText as m, initReveal as n, FacetsRenderedEvent as o, parseHTML as p, cssTimeMs as q, rootUrl as r, stepQuantity as s, themeString as t, replayReveal as u, parseMoney as v, formatMoney as w, onVisible as x, CountdownEndedEvent as y, reducedMotion as z };
